/* eslint-disable @typescript-eslint/naming-convention */
import {
    clamp,
    cloneDeep,
    difference,
    indexOf,
    isArray,
    isNull,
    isNumber,
    isString,
    isUndefined,
    maxBy,
    minBy,
    pull,
    pullAll,
    range,
    remove,
    toInteger,
} from 'lodash';
import { v4 as uuid } from 'uuid';
import { convertToPolygon, getRotatePosition, type IShape } from '@/utils/collision-kit';
import { isHotKeyPress } from '@/utils/event-kit';
import {
    getCenterPointForOrigin,
    getGroupLayerProps,
    getMainLayerRenderSize,
    getNewCenterPoint,
} from '@/utils/layer-kit';
import { getActualAngle, insertToArrByIndex, insertWithIndex, rotatePointWithFocus } from '@/utils/math-kit';
import { traverseTree, traverseTrees } from '@/utils/tree-kit';
import {
    BaseLayerViewType,
    DirectionType,
    LAYER_MIN_SIZE,
    LayerType,
    type IBaseOperateData,
    type IBaseOperateFlag,
    type ICorrectLayerMinSize,
    type IExtraInfo,
    type ILayerOriginInfo,
    type ILayerProps,
    type IPoint,
    type IPosition,
    type ISize,
    type ScaleInfo,
} from '@/version-types';
import {
    convertMaterialIdsToMap,
    getPointRect,
    isLayerProps,
    type IActiveInfo,
    type IDelLayerData,
    type IExportLayerOriginInfo,
    type IReplaceLayerResult,
    type IService,
    type IStageBaseOperateData,
    type TBBox,
    type TMat2D,
    type XY,
} from '../../shared';
import { EdgelessFrame } from '../canvas/edgeless-frame';
import type { EdgelessObject } from '../canvas/edgeless-object';
import { EventGenerator } from '../events';
import type { ServiceManager } from '../service-manager';

interface IPreActiveData {
    layer: ILayerProps | null;
    pickLayer: ILayerProps | null;
}

/** 所有舞台重新渲染事件名称 */
const rerenderAllFramesEventName = 'edgeless:frame:1';
/** 单个舞台重新渲染事件名称 */
// const rerenderFrameEventName = 'edgeless:frame:2';

const layerMoveOrRotateEventName = 'edgeless:frame:3';
const baseOperateEventName = 'edgeless:frame:4';
const beforeBaseOperateEventName = 'edgeless:frame:5';
const beforeLayerMoveEndEventName = 'edgeless:frame:before-layer-move-end';
const activeUpdateEventName: string = 'edgeless:frame:6';
const layerClickEventName: string = 'edgeless:frame:7';
const layerDoubleClickEventName: string = 'edgeless:frame:8';

const activeInfoEventName: string = 'frame:activeInfo';
const __frameCompleteEventName: string = 'edgeless:frame:10';
const __framePreviewCompleteEventName: string = 'edgeless:frame:11';

const __activeIndexChangeEventName: string = 'edgeless:frame:12';

const sortLayerEventName = 'edgeless:frame:13';
const runtimeLayerChangeEventName = 'edgeless:frame:14';
const runtimeLayerDeleteEventName = 'edgeless:frame:15';
const frameSelectionChangeEventName = 'edgeless:frame:16';
const addExtraInfosEventName = 'edgeless:frame:17';
const frameLayoutCompleteEventName = 'edgeless:frame:18';
/** 图层替换完成后的统一排版通知 */
const layersReplacedEventName = 'edgeless:frame:layers-replaced';

/**
 * 舞台管理服务
 */
export class FrameService implements IService {
    /**
     * 多舞台，原始画布图层列表
     * [[画布1]=0, [画布2]=1]
     * 每次添加画布，只会增加这个列表，不会做删除
     * 对应的索引叫: originIndex
     */
    private __frameLayerIdsList: string[][] = [];
    /** 画布的额外信息，
     * 长度与 __frameLayerIdsList 一致
     */
    private __extraInfos: IExtraInfo[] = [];
    /**
     * 需要渲染的舞台列表
     * [frameId=1, frameId=0]
     * 对应的索引叫: index
     * 当前激活的舞台索引: activeIndex
     */
    private __renderFrameIdsList: number[] = [];
    /** runtime-only 临时图层 */
    private __runtimeLayerMap: Map<number, Map<string, ILayerProps>> = new Map();
    private __replaceLayerDepth = 0;
    private __replacedFrameIds = new Set<number>();

    private __preActiveData: IPreActiveData | null = null;
    private __preActiveLayerId?: string | null = null;
    private __activeObject: EdgelessObject | null = null;
    private __activeInfo: IActiveInfo | null = null;

    private __frames: Map<number, EdgelessFrame>;
    /** 无限画布模式下，选中的画布ID列表 */
    private __frameSelectionFrameIds: number[] = [];

    /** 激活的画布索引 */
    private __activeIndex: number = 0;

    /** 上一次激活的画布ID */
    private __preActiveFrameId: number = 0;
    private __preActiveIndex: number = 0;

    /** 初始化后的首次非空布局是否需要处理长画布起始位置 */
    private __shouldAlignInitialLongImageViewport = true;

    /** 粘性信息 */
    private __stickyInfo: {
        /** 粘性移入 frameId 索引 */
        in: number;
        /** 粘性移出 frameId 索引 */
        out: number;
        /** 粘性图层 ID */
        layerId: string;
    } | null = null;

    constructor(readonly service: ServiceManager) {
        const eventGenerator = new EventGenerator('frame', this, service);
        console.log('frame attrs ==>', eventGenerator.attrs);
        this.__frames = new Map();
    }

    onInit() {
        this.__frameLayerIdsList.length = 0;
        this.__extraInfos.length = 0;
        this.__renderFrameIdsList.length = 0;
        this.__runtimeLayerMap.clear();
        this.__replaceLayerDepth = 0;
        this.__replacedFrameIds.clear();
        this.__preActiveData = null;
        this.__preActiveLayerId = null;
        this.__activeObject = null;
        this.__frameSelectionFrameIds = [];
        this.__activeIndex = 0;
        this.__preActiveFrameId = 0;
        this.__preActiveIndex = 0;
        this.__shouldAlignInitialLongImageViewport = true;

        this.clearSticky();
        this.__frames.clear();

        this.setHoverId('');
        this.setHoverFrameId(null);
        this.setActiveInfo(null);
        this.setSelectedIds(new Set());

        this.setActiveIndex(0);
        this.setPickActiveFrame(false);
    }

    onBindEvent() {
        // 长图偏移参与 Frame 边界缓存，切换模式后须先更新边界再计算视口和操作层位置
        this.service.edgelessCanvas.onRenderModeChange(() => {
            const renderFrameIds = this.getRenderFrameIds();
            this.__setFrames(renderFrameIds);
            renderFrameIds.forEach((frameId) => this.service.edgelessCanvas.addDirtyFrameId(frameId));
            this.service.edgelessCanvas.calcViewportTransform();
            this.service.edgelessCanvas.refreshScrollInfo();
            this.service.edgelessCanvas.requestRenderAll();
            this.__emitFrameLayoutComplete(renderFrameIds);
        }, 999);

        // 添加、删除舞台，更新 frame 数据
        this.onRerenderAll((renderFrameIds, diff) => {
            const hoverFrameId = this.hoverFrameId;
            if (isNumber(hoverFrameId) && !renderFrameIds.includes(hoverFrameId)) {
                this.setHoverFrameId(null);
            }
            this.__pruneFrameSelection(renderFrameIds);
            // console.log('setFrames onRerenderAll', renderStageList);
            requestAnimationFrame(() => {
                this.__setFrames(renderFrameIds);
                diff.forEach((frameId) => {
                    this.service.edgelessCanvas.addDirtyFrameId(frameId);
                });
                this.service.edgelessCanvas.calcViewportTransform();
                this.__alignInitialLongImageViewport(renderFrameIds);
                this.service.edgelessCanvas.requestRenderAll();
                this.__emitFrameLayoutComplete(renderFrameIds);
                // console.log('onRerenderAll 1 - 1', renderFrameIds);
            });
        }, 999);

        // 监听激活舞台变化，重新计算缩放和平移
        this.onActiveIndexChange(() => this.service.edgelessCanvas.calcViewportTransform());
        this.onResize((frameId) => {
            if (this.service.edgelessCanvas.isLongImageMode) {
                this.__frames.forEach((frame) => {
                    frame.update();
                });
            } else {
                this.get(frameId)?.update();
            }
            // 先更新边界，避免新缩放比例与旧边界混用导致画布偏移
            this.service.edgelessCanvas.calcViewportTransform();
        }, 999);

        const emitActiveUpdate = (currentData: IPreActiveData | null) => {
            if (
                currentData &&
                (currentData.layer !== this.__preActiveData?.layer ||
                    currentData.pickLayer !== this.__preActiveData?.pickLayer)
            ) {
                this.__preActiveData = currentData;
                this.__emitActiveLayerDataUpdate(currentData.layer, currentData.pickLayer);
            }
            this.__preActiveLayerId = currentData?.layer?.id;
        };

        this.onActiveInfoChange(() => {
            const layer = this.getActiveLayer();
            let pickLayer = this.getActivePickLayer();

            const preLayerId = this.__preActiveData?.pickLayer?.id || this.__preActiveData?.layer?.id;
            if (!layer || preLayerId !== pickLayer?.id) {
                const layerIds: string[] = [];
                let isNull = true;
                if (pickLayer) {
                    traverseTree(pickLayer, (item) => layerIds.push(item.id));
                    const activeObject = this.service.edgelessCanvas.getByLayerId(pickLayer.id)?.active();
                    if (activeObject) {
                        if (this.__activeObject !== activeObject) {
                            this.__activeObject?.inactive();
                        }

                        this.__activeObject = activeObject;
                        isNull = false;
                    }
                }

                if (isNull) {
                    this.__activeObject?.inactive();
                    this.__activeObject = null;
                }
                // this.service.edgelessCanvas.setActive(layerIds);
            }

            pickLayer = layer === pickLayer ? null : pickLayer;
            emitActiveUpdate({ layer, pickLayer });
        });

        this.service.flatLayer.onChange((layer) => {
            if (!layer || !this.activeInfo) return;
            if (layer.id === this.__preActiveLayerId) {
                this.__preActiveLayerId = null;
                return;
            }

            const activeLayer = this.getActiveLayer();
            let currentData: IPreActiveData | null = null;
            if (activeLayer?.id === layer.id) {
                // 父组更新不代表退出组内选中，否则图片/文字工具栏会被切换成组工具栏。
                currentData = { layer: activeLayer, pickLayer: this.getActivePickLayer(false) || null };
            } else if (activeLayer) {
                const pickLayer = this.getActivePickLayer(false);
                if (pickLayer?.id === layer.id) {
                    currentData = { layer: activeLayer, pickLayer: pickLayer };
                }
            }
            if (currentData) {
                emitActiveUpdate(currentData);
            }
        });

        this.onPickActiveFrameChange((pickActiveFrame) => {
            if (pickActiveFrame) {
                this.delActiveLayer();
                this.delHoverId();
            }
        });
    }

    private __getRuntimeLayerStore(frameId: number, create = false) {
        if (!this.__runtimeLayerMap.has(frameId) && create) {
            this.__runtimeLayerMap.set(frameId, new Map());
        }
        return this.__runtimeLayerMap.get(frameId);
    }

    private __emitRuntimeLayerChange(
        layer: ILayerProps | null,
        onlyUpdateBaseInfo: boolean,
        args: { isAdd?: boolean; isDelete?: boolean; deleteIds?: string[]; frameId: number },
    ) {
        this.service.events.emit(runtimeLayerChangeEventName, layer, onlyUpdateBaseInfo, args);
    }

    onRuntimeLayerChange(
        fn: (
            layer: ILayerProps | null,
            onlyUpdateBaseInfo: boolean,
            args: { isAdd?: boolean; isDelete?: boolean; deleteIds?: string[]; frameId: number },
        ) => void,
        weight = 0,
    ) {
        return this.service.events.on(runtimeLayerChangeEventName, fn, weight);
    }

    onRuntimeLayerDelete(fn: (args: { layerIds: string[]; frameId: number }) => void, weight = 0) {
        return this.service.events.on(runtimeLayerDeleteEventName, fn, weight);
    }

    addRuntimeLayer(layer: ILayerProps, destOriginIndex?: number, onlyUpdateBaseInfo = false) {
        const frameId = isNumber(destOriginIndex) ? destOriginIndex : (layer.__originIndex ?? this.getActiveFrameId());
        const store = this.__getRuntimeLayerStore(frameId, true)!;
        layer.__originIndex = frameId;
        layer.__isTempLayer = true;
        const isAdd = !store.has(layer.id);
        store.set(layer.id, layer);
        this.__emitRuntimeLayerChange(layer, onlyUpdateBaseInfo, { isAdd, isDelete: false, frameId });
        return layer;
    }

    getRuntimeLayer(layerId: string): ILayerProps | null;
    getRuntimeLayer(frameId: number): ILayerProps[];
    getRuntimeLayer(target: string | number) {
        if (isNumber(target)) {
            return Array.from(this.__getRuntimeLayerStore(target)?.values() ?? []);
        }

        for (const store of this.__runtimeLayerMap.values()) {
            const layer = store.get(target);
            if (layer) {
                return layer;
            }
        }
        return null;
    }

    removeRuntimeLayer(layerId: string, frameId?: number) {
        let targetFrameId = isNumber(frameId) ? frameId : null;
        if (!isNumber(targetFrameId)) {
            for (const [runtimeFrameId, store] of this.__runtimeLayerMap.entries()) {
                if (store.has(layerId)) {
                    targetFrameId = runtimeFrameId;
                    break;
                }
            }
        }
        if (!isNumber(targetFrameId)) return false;
        const store = this.__getRuntimeLayerStore(targetFrameId);
        if (!store?.has(layerId)) return false;
        store.delete(layerId);
        if (store.size === 0) {
            this.__runtimeLayerMap.delete(targetFrameId);
        }
        this.__emitRuntimeLayerChange(null, false, {
            isDelete: true,
            deleteIds: [layerId],
            frameId: targetFrameId,
        });
        this.service.events.emit(runtimeLayerDeleteEventName, { layerIds: [layerId], frameId: targetFrameId });
        return true;
    }

    /** 选中激活画布，ui 上的选中状态 */
    readonly pickActiveFrame: boolean = false;
    declare getPickActiveFrame: () => boolean;
    declare setPickActiveFrame: (pickActiveFrame: boolean) => void;
    declare onPickActiveFrameChange: (fn: (pickActiveFrame: boolean) => void, weight?: number) => number;

    /** 悬浮操作的图层id */
    readonly hoverId: string = '';
    declare getHoverId: () => string;
    declare setHoverId: (hoverId: string) => void;
    declare onHoverIdChange: (fn: (hoverId: string) => void, widget?: number) => number;

    /** 悬浮操作的舞台 ID */
    readonly hoverFrameId: number | null = null;
    declare getHoverFrameId: () => number | null;
    declare setHoverFrameId: (hoverFrameId: number | null) => void;
    declare onHoverFrameIdChange: (fn: (hoverFrameId: number | null) => void, weight?: number) => number;

    /** 激活的图层信息 */
    get activeInfo() {
        return this.__activeInfo;
    }

    getActiveInfo() {
        return this.__activeInfo;
    }

    setActiveInfo(activeInfo: IActiveInfo | null) {
        const isSameActiveInfo = this.__activeInfo === activeInfo;
        this.__activeInfo = activeInfo;
        this.__syncFrameSelectionByActiveInfo(activeInfo);
        if (isSameActiveInfo) return;
        this.service.events.emit(activeInfoEventName, activeInfo);
    }

    onActiveInfoChange(fn: (activeInfo: IActiveInfo | null) => void, widget?: number) {
        return this.service.events.on(activeInfoEventName, fn, widget);
    }

    /** 临时选中的图层id列表 */
    readonly selectedIds: Set<string> = new Set();
    declare getSelectedIds: () => Set<string>;
    declare setSelectedIds: (selectedIds: Set<string>) => void;
    declare onSelectedIdsChange: (fn: (selectedIds: Set<string>) => void, widget?: number) => number;

    /** 激活的画布索引 */
    get activeIndex() {
        return this.__activeIndex;
    }
    /** 激活的画布ID */
    get activeFrameId() {
        return this.getActiveFrameId();
    }

    /** 监听舞台尺寸变化 */
    onResize(fn: (frameId: number) => void, weight = 0) {
        return this.service.flatLayer.onFrameResize(fn, weight);
    }

    /** 获取激活的画布ID */
    getActiveFrameId() {
        return this.indexToFrameId(this.activeIndex);
    }

    /** 获取激活的画布索引 */
    getActiveIndex() {
        return this.activeIndex;
    }

    /** 监听激活画布索引变化 */
    onActiveIndexChange(fn: (activeIndex: number) => void, weight?: number) {
        return this.service.events.on(__activeIndexChangeEventName, fn, weight);
    }
    __emitActiveIndexChange(activeIndex: number) {
        this.service.events.emit(__activeIndexChangeEventName, activeIndex);
    }

    /**
     * 设置激活的画布索引
     * @param activeIndex - 激活的画布索引
     * @returns
     */
    setActiveIndex(activeIndex: number) {
        const { edgelessCanvas } = this.service;
        if (edgelessCanvas.isEdgelessMode) return false;
        const newFrameId = this.indexToFrameId(activeIndex);
        if (this.__preActiveFrameId !== newFrameId || this.__preActiveIndex !== activeIndex) {
            this.__preActiveIndex = activeIndex;
            this.__preActiveFrameId = newFrameId;
            this.__activeIndex = activeIndex;

            // 重置激活图层
            const activeLayer = this.getActiveLayer();
            if (activeLayer) {
                const frameId = this.getLayerFrameId(activeLayer.id);
                if (isNumber(frameId) && this.frameIdToIndex(frameId) !== activeIndex) {
                    this.delActiveLayer();
                }
            }
            // 重置悬浮图层
            this.delHoverId();
            // 设置自动缩放
            if (edgelessCanvas.isSingleMode) {
                edgelessCanvas.setIsAutoScale(true);
            }

            this.__emitActiveIndexChange(activeIndex);

            // console.log('setActiveIndex=>', {
            //     frameId: { n: newFrameId, p: this.__preActiveFrameId },
            //     index: { n: activeIndex, p: this.__preActiveIndex },
            // });

            edgelessCanvas.requestRenderAll();
        }
        return true;
    }

    /** 根据图层id设置激活的舞台索引 */
    setActiveIndexByLayerId(layerId: string) {
        const frameId = this.getLayerFrameId(layerId);
        if (isNumber(frameId)) {
            this.setActiveIndex(this.frameIdToIndex(frameId));
        }
    }

    /**
     * 获取激活的画布尺寸
     * @param index - 画布索引
     * @returns 画布尺寸
     */
    getActiveFrameSize(index?: number): ISize {
        const stageIndex = index ?? this.getActiveIndex();
        return this.getSizeByIndex(stageIndex);
    }

    /** 获取激活的画布所有图层 */
    getActiveFrameLayers() {
        const stageIndex = this.getActiveIndex();
        return this.getLayersByIndex(stageIndex);
    }

    /** 获取选中的舞台索引 */
    getFrameSelection(): number[] {
        return this.getFrameSelectionFrameIds()
            .map((frameId) => this.frameIdToIndex(frameId))
            .filter((index) => index >= 0)
            .sort((a, b) => a - b);
    }

    /** 获取选中的舞台ID */
    getFrameSelectionFrameIds(): number[] {
        return this.__frameSelectionFrameIds.filter((frameId) => this.__isSelectableFrameId(frameId));
    }

    /** 获取最后一个选中的舞台ID */
    getFrameSelectionFrameId() {
        const frameIds = this.getFrameSelectionFrameIds();
        return frameIds[frameIds.length - 1] ?? null;
    }

    /** 设置选中的舞台索引 */
    setFrameSelection(stageIndexes: number[]) {
        const frameIds = stageIndexes
            .map((index) => this.__renderFrameIdsList[index])
            .filter((frameId): frameId is number => isNumber(frameId));
        this.setFrameSelectionFrameIds(frameIds);
    }

    /** 设置单个选中的舞台ID */
    setFrameSelectionFrameId(frameId: number | null) {
        if (isNull(frameId)) {
            this.clearFrameSelection();
            return;
        }
        this.setFrameSelectionFrameIds([frameId]);
    }

    /** 设置选中的舞台ID */
    setFrameSelectionFrameIds(frameIds: number[]) {
        const nextFrameIds = Array.from(new Set(frameIds)).filter((frameId) => this.__isSelectableFrameId(frameId));
        const oldFrameIds = this.__frameSelectionFrameIds;
        if (
            oldFrameIds.length === nextFrameIds.length &&
            oldFrameIds.every((frameId, index) => frameId === nextFrameIds[index])
        ) {
            return;
        }

        this.__frameSelectionFrameIds = nextFrameIds;
        this.__emitFrameSelectionChange();

        // if (nextFrameIds.length === 0) {
        //     debugger;
        // }

        // console.log('setFrameSelection=>', nextFrameIds);
    }

    filterFrameSelectionGroupFrameIds(frameIds: number[], targetFrameId = frameIds[0]) {
        if (!isNumber(targetFrameId)) return [];

        const selectableFrameIds = frameIds.filter((frameId) => this.__isSelectableFrameId(frameId));
        const targetIsPPTFrame = this.service.edgelessPPT.isPPTFrame(targetFrameId);
        if (!targetIsPPTFrame) {
            return selectableFrameIds.filter((frameId) => !this.service.edgelessPPT.isPPTFrame(frameId));
        }

        const defaultTaskId = this.service.agentLayout.getEnable()
            ? this.service.agentLayout.getDefaultPPTTaskId()
            : undefined;
        const targetTaskKey = this.service.edgelessPPT.getPPTFrameGroupTaskId(targetFrameId, defaultTaskId) || '';

        return selectableFrameIds.filter((frameId) => {
            if (!this.service.edgelessPPT.isPPTFrame(frameId)) return false;

            return (this.service.edgelessPPT.getPPTFrameGroupTaskId(frameId, defaultTaskId) || '') === targetTaskKey;
        });
    }

    /** 切换舞台选中状态 */
    toggleFrameSelectionFrameId(frameId: number) {
        if (!this.__isSelectableFrameId(frameId)) return;

        const frameIds = this.getFrameSelectionFrameIds();
        if (frameIds.includes(frameId)) {
            this.setFrameSelectionFrameIds(frameIds.filter((id) => id !== frameId));
        } else {
            this.setFrameSelectionFrameIds([...this.filterFrameSelectionGroupFrameIds(frameIds, frameId), frameId]);
        }
    }

    /** 清空舞台选择 */
    clearFrameSelection() {
        this.setFrameSelectionFrameIds([]);
    }

    private __syncFrameSelectionByActiveInfo(activeInfo: IActiveInfo | null) {
        const activeLayerId = activeInfo?.info.pickId || activeInfo?.info.id;
        const activeLayerFrameId = activeLayerId ? this.get(activeLayerId)?.frameId : null;
        if (isNumber(activeLayerFrameId)) {
            this.setFrameSelectionFrameIds([activeLayerFrameId]);
            return;
        }

        if (this.getPickActiveFrame()) return;

        this.clearFrameSelection();
    }

    onFrameSelectionChange(callback: (stageIndexes: number[], frameIds: number[]) => void) {
        return this.service.events.on(frameSelectionChangeEventName, callback);
    }

    private __emitFrameSelectionChange() {
        this.service.events.emit(
            frameSelectionChangeEventName,
            this.getFrameSelection(),
            this.getFrameSelectionFrameIds(),
        );
    }

    private __pruneFrameSelection(renderFrameIds = this.__renderFrameIdsList) {
        const nextFrameIds = this.__frameSelectionFrameIds.filter((frameId) =>
            this.__isSelectableFrameId(frameId, renderFrameIds),
        );
        if (
            nextFrameIds.length === this.__frameSelectionFrameIds.length &&
            nextFrameIds.every((frameId, index) => frameId === this.__frameSelectionFrameIds[index])
        ) {
            return;
        }

        this.__frameSelectionFrameIds = nextFrameIds;
        this.__emitFrameSelectionChange();
    }

    private __isSelectableFrameId(frameId: number, renderFrameIds = this.__renderFrameIdsList) {
        if (!renderFrameIds.includes(frameId)) return false;
        return !this.service.edgelessCanvas.isEdgelessMode || frameId !== 0;
    }

    /** 滚动到激活舞台（将激活舞台顶对齐视口顶） */
    scrollToActiveFrame(duration = 300) {
        const { edgelessCanvas } = this.service;
        if (!edgelessCanvas.isLongImageMode) return;

        // frame 可能在 requestAnimationFrame 中才被创建（__setFrames），
        // 延迟到下一帧确保 frame 已存在且布局已更新
        requestAnimationFrame(() => {
            // console.log('scrollToActiveFrame=>', this.activeFrameId);
            this.__doScrollToActiveFrame(duration);
        });
    }

    /** 首次非空布局只处理一次；非长画布也会消费初始化状态 */
    private __alignInitialLongImageViewport(renderFrameIds: number[]) {
        if (!this.__shouldAlignInitialLongImageViewport || !renderFrameIds.length) return;
        this.__shouldAlignInitialLongImageViewport = false;
        this.service.edgelessCanvas.alignLongImageViewportToStart();
    }

    private __doScrollToActiveFrame(duration: number) {
        const { edgelessCanvas } = this.service;

        edgelessCanvas.refreshScrollInfo();
        const scrollInfo = edgelessCanvas.scrollInfo;

        if (!scrollInfo || !scrollInfo.vertical.needScrollbar) {
            edgelessCanvas.calcViewportTransform();
            return;
        }

        const frame = this.get(this.activeFrameId);
        if (!frame) return;

        frame.update();
        const scale = edgelessCanvas.zoom;
        const padV = edgelessCanvas.fitPadding[0];
        const currentTranslateY = edgelessCanvas.translateY;
        const { offset } = scrollInfo.vertical;

        const frameScreenTop = frame.top * scale + currentTranslateY;
        const adjustY = padV - frameScreenTop;
        const clampedAdjustY = clamp(adjustY, -offset.bottom, offset.top);
        const targetTranslateY = currentTranslateY + clampedAdjustY;

        const startTime = Date.now();
        const transition = () => {
            const now = Date.now();
            const progress = Math.min(1, (now - startTime) / duration);
            const eased = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            const isComplete = progress >= 1;
            const destY = isComplete
                ? targetTranslateY
                : currentTranslateY + (targetTranslateY - currentTranslateY) * eased;

            const vpt: TMat2D = [...edgelessCanvas.viewportTransform];
            vpt[5] = destY;
            edgelessCanvas.setViewportTransform(vpt);

            if (!isComplete) {
                requestAnimationFrame(transition);
            }
        };

        transition();
    }

    /** 添加/更新舞台 */
    addStages(
        data: ILayerProps[] | ILayerProps[][],
        args: {
            /** 舞台索引 */
            stageIndex?: number;
            /** 是否激活新舞台 */
            activeNewStage?: boolean;
            /** 是否缩放宽度 */
            scaleWidth?: boolean | number;
            /** 替换激活舞台 */
            replaceActiveStage?: boolean;
            /** 替换所有舞台 */
            replaceAllStage?: boolean;
            /** 指定插入舞台的原始索引 */
            originIndexes: number[];
        },
    ): {
        addList: number[];
    } & IStageBaseOperateData {
        const flatLayer = this.service.flatLayer;
        const { activeNewStage, scaleWidth, replaceActiveStage, replaceAllStage, originIndexes } = args;
        const addOriginIndexList: number[] = [];
        const oldStageList = this.getRenderFrameIds();
        const oldActiveStageIndex = this.activeIndex;

        let { stageIndex } = args;
        let originIndex = 0;
        let newStageList = oldStageList;
        let newActiveStageIndex = oldActiveStageIndex;

        const setRenderSortIdsArr = (i: number, index?: number) => {
            if (i !== 0) return;
            originIndex = isNumber(index) ? index : this.getLayerIdsList().length;
            addOriginIndexList.push(originIndex);
            if (isNumber(stageIndex)) {
                stageIndex = clamp(stageIndex, 0, this.getFrameIdsLength() - 1);
                const stageList = insertToArrByIndex(this.getRenderFrameIds(), stageIndex, originIndex, false);
                this.setFrameIds(stageList);
            } else {
                this.addFrameIds(originIndex);
            }
            newStageList = cloneDeep(this.getRenderFrameIds());
            newActiveStageIndex = newStageList.indexOf(originIndex);
        };

        data.forEach((item: ILayerProps | ILayerProps[], i) => {
            // console.log('item===>', item, i);
            if (isArray(item)) {
                if (item.length) {
                    item.forEach((layer, j) => {
                        setRenderSortIdsArr(j, originIndexes[i]);
                        this.addOrUpdateLayer(layer, originIndex);
                        // console.log('l===>', { i, j, originIndex });
                    });
                } else {
                    setRenderSortIdsArr(0, originIndexes[i]);
                    this.getFrameIdAndLayerIds(originIndex, true);
                    // console.log('i===>', { i, originIndex });
                }
            } else {
                setRenderSortIdsArr(i, originIndexes[0]);
                this.addOrUpdateLayer(item, originIndex);
            }
        });

        if (activeNewStage) {
            this.setActiveIndex(newActiveStageIndex);
        }

        // 换算宽度
        if (scaleWidth) {
            const newOriginIndex = this.indexToFrameId(newActiveStageIndex);
            const bgLayer = this.getBackgroundLayer(newOriginIndex);
            if (bgLayer) {
                const { width, height } = bgLayer;
                const stageIndex = this.activeIndex;
                const size = this.getSizeByIndex(stageIndex);
                const currentWidth = isNumber(scaleWidth) ? scaleWidth : size.width;
                if (width !== currentWidth) {
                    const oldSize = { width, height };
                    const newSize = {
                        width: currentWidth,
                        height: toInteger(currentWidth / (width / height)),
                    };
                    const layers = cloneDeep(this.getLayers(newOriginIndex));
                    this.scaleLayers(layers, oldSize, newSize, false, true, false, (layer) => {
                        flatLayer.set(layer.id, layer, {
                            updateDependencies: !layer.parentId,
                        });
                    });
                }
            }
        }

        // 替换当前模版
        if (replaceActiveStage) {
            newActiveStageIndex = oldActiveStageIndex;
            newStageList = cloneDeep(oldStageList);
            newStageList[oldActiveStageIndex] = originIndex;
            this.setFrameIds(newStageList);
            // 索引不变但 frameId 已被替换，需要重新广播一次，避免外部持有失效的旧 frameId
            this.setActiveIndex(newActiveStageIndex);
        }

        // 替换全部模版
        if (replaceAllStage) {
            newActiveStageIndex = 0;
            newStageList = difference(newStageList, oldStageList);
            this.setFrameIds(newStageList);
            this.setActiveIndex(newActiveStageIndex);
        }

        return {
            addList: addOriginIndexList,
            oldIds: oldStageList,
            oldIndex: oldActiveStageIndex,
            newIds: newStageList,
            newIndex: newActiveStageIndex,
        };
    }

    /** 删除舞台 */
    delStage(stageIndex: number): IStageBaseOperateData {
        const len = this.getFrameIdsLength();
        const oldIndex = clamp(stageIndex, 0, len - 1);
        const oldIds = this.getRenderFrameIds();
        this.removeFrameIds(oldIds[oldIndex]);
        const newIds = this.getRenderFrameIds();
        // 删除后移动到下一张
        let newIndex;
        if (oldIndex >= oldIds.length - 1) {
            // 如果删除的是最后一张,选中新的最后一张
            newIndex = newIds.length - 1;
        } else {
            // 否则选中下一张
            newIndex = oldIndex;
        }
        // console.log('delStage=>', { oldIndex, newIndex });

        this.setActiveIndex(newIndex);
        return {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
        };
    }

    /** 批量删除舞台 */
    batchDelStage(indices: number[]): IStageBaseOperateData | null {
        const len = this.getFrameIdsLength();
        const deleteIndices = [...indices];
        // 如果要删除的数量等于总数量，至少保留一个舞台
        if (len === deleteIndices.length) {
            deleteIndices.pop(); // 移除最后一个要删除的索引
        }
        if (len === 1 || deleteIndices.length === 0) {
            return null;
        }

        // 按照索引从大到小排序，避免删除时索引变化
        const sortedIndices = [...deleteIndices].sort((a, b) => b - a);
        // 记录删除前的状态
        const oldIds = this.getRenderFrameIds();
        const oldIndex = this.activeIndex;

        const renderStageList = this.getRenderFrameIds();
        // 执行删除操作
        sortedIndices.forEach((index) => {
            const num = renderStageList[index];
            pull(renderStageList, num);
        });

        // 更新后的状态
        const newIds = cloneDeep(renderStageList);
        this.setFrameIds(newIds);
        // 计算新的激活索引
        let newIndex;
        const maxDeletedIndex = Math.max(...deleteIndices);

        if (maxDeletedIndex >= oldIds.length - 1) {
            // 如果删除的包含最后一张,选中新的最后一张
            newIndex = newIds.length - 1;
        } else {
            // 否则选中删除区间后的第一张
            const minDeletedIndex = Math.min(...deleteIndices);
            newIndex = minDeletedIndex;
        }
        this.setActiveIndex(newIndex);

        return {
            oldIds,
            oldIndex,
            newIds,
            newIndex,
        };
    }

    /** 交换舞台 */
    swapStages(
        stagesIndex: number[],
        destIndex: number,
        sourceIndex: number,
        activeIndex?: number,
    ): IStageBaseOperateData {
        activeIndex = activeIndex ?? stagesIndex[0];
        const oldIds = this.getRenderFrameIds();
        const oldOriginIndexes = stagesIndex.map((index) => oldIds[index]);
        const renderFrameIdsWithoutSource = [...oldIds];
        pull(renderFrameIdsWithoutSource, ...oldOriginIndexes);
        let sortUp = sourceIndex > destIndex; // 是否向上移动
        let insertIndex = renderFrameIdsWithoutSource.indexOf(oldIds[destIndex]);
        // 如果目标索引在已选范围内
        if (stagesIndex.includes(destIndex)) {
            const newIds = renderFrameIdsWithoutSource;
            // 获取已选位置的完整连续范围
            const selectionRange = range(stagesIndex[0], stagesIndex[stagesIndex.length - 1] + 1);
            // 获取已选位置的完整连续范围中未被选中的元素数组
            const excludeRange = difference(selectionRange, stagesIndex);
            if (destIndex > excludeRange[excludeRange.length - 1]) {
                const destIndexOld = excludeRange[excludeRange.length - 1];
                const destOriginIndexOld = oldIds.indexOf(destIndexOld);
                insertIndex = newIds.indexOf(destOriginIndexOld);
                sortUp = false;
            } else if (destIndex < excludeRange[0]) {
                const prevDestIndexOld = stagesIndex[0] - 1;
                if (prevDestIndexOld < 0) {
                    insertIndex = 0;
                } else {
                    const prevDestOriginIndexOld = newIds[prevDestIndexOld];
                    insertIndex = newIds.indexOf(prevDestOriginIndexOld) + 1;
                }
                sortUp = true;
            }
        }
        const list = insertToArrByIndex([...renderFrameIdsWithoutSource], insertIndex, oldOriginIndexes, sortUp);
        this.setFrameIds(list);
        const newIds = this.getRenderFrameIds();
        const newIndex = newIds.indexOf(oldIds[activeIndex]);
        this.setActiveIndex(newIndex);

        return {
            oldIds,
            oldIndex: activeIndex,
            newIds,
            newIndex,
            oldStageSelection: stagesIndex,
            newStageSelection: oldOriginIndexes.map((index) => newIds.indexOf(index)),
        };
    }

    /** 恢复画布 */
    restoreFrame(activeIndex: number, renderFrameIds: number[]): void {
        this.setFrameIds(renderFrameIds);
        this.setActiveIndex(activeIndex);
    }

    /** 克隆画布 */
    cloneFrame(index: number) {
        const layers = cloneDeep(this.getLayersByIndex(index));
        return this.resetLayerIds(layers);
    }

    /** 重置图层id */
    resetLayerIds(layers: ILayerProps[]) {
        traverseTrees(layers, (layer) => {
            layer.id = uuid();
            layer.children?.forEach((child) => {
                child.parentId = layer.id;
            });
        });
        return layers;
    }

    /** 替换激活舞台 */
    replaceActiveFrame(data: ILayerProps[], scaleWidth: boolean | number = false, originIndexes: number[] = []) {
        return this.addStages(data, {
            replaceActiveStage: true,
            scaleWidth,
            originIndexes,
        });
    }

    /** 替换所有舞台 */
    replaceAllFrames(data: ILayerProps[][], originIndexes: number[] = []) {
        return this.addStages(data, {
            replaceAllStage: true,
            originIndexes,
        });
    }

    /** 监听帧渲染完成事件 */
    onRenderComplete(callback: (args: { frameId: number; canvas: HTMLCanvasElement }) => void) {
        return this.service.events.on(__frameCompleteEventName, callback);
    }
    __emitRenderComplete(args: { frameId: number; canvas: HTMLCanvasElement }) {
        this.service.events.emit(__frameCompleteEventName, args);
    }

    /** 监听帧预览渲染完成事件 */
    onPreviewComplete(callback: (args: { frameId: number; canvas: HTMLCanvasElement }) => void) {
        return this.service.events.on(__framePreviewCompleteEventName, callback);
    }
    __emitPreviewComplete(args: { frameId: number; canvas: HTMLCanvasElement }) {
        this.service.events.emit(__framePreviewCompleteEventName, args);
    }

    /**
     * 监听激活图层数据更新、激活图层切换
     */
    onActiveLayerDataUpdate(fn: (activeLayer: ILayerProps | null, pickLayer: ILayerProps | null) => void, weight = 0) {
        return this.service.events.on(activeUpdateEventName, fn, weight);
    }
    __emitActiveLayerDataUpdate(activeLayer: ILayerProps | null, pickLayer: ILayerProps | null) {
        this.service.events.emit(activeUpdateEventName, activeLayer, pickLayer);
    }

    /** 监听图层点击事件 */
    onLayerClick(fn: (layerId: string, point: IPoint) => void, weight = 0) {
        return this.service.events.on(layerClickEventName, fn, weight);
    }
    __emitLayerClickEvent(layerId: string, point: IPoint) {
        this.service.events.emit(layerClickEventName, layerId, point);
    }

    /** 监听图层双击事件 */
    onLayerDoubleClick(fn: (layerId: string, point: IPoint) => void, weight = 0) {
        return this.service.events.on(layerDoubleClickEventName, fn, weight);
    }
    __emitLayerDoubleClickEvent(layerId: string, point: IPoint) {
        this.service.events.emit(layerDoubleClickEventName, layerId, point);
    }

    /** 监听移动/旋转事件 */
    onLayerMoveOrRotate(
        fn: (layerId: string, operateType: BaseLayerViewType, args: IBaseOperateFlag, data?: IBaseOperateData) => void,
        weight = 0,
    ) {
        return this.service.events.on(layerMoveOrRotateEventName, fn, weight);
    }
    __emitLayerMoveOrRotate(
        layerId: string,
        operateType: BaseLayerViewType,
        args: IBaseOperateFlag,
        data?: IBaseOperateData,
    ) {
        this.service.events.emit(layerMoveOrRotateEventName, layerId, operateType, args, data);
    }

    /** 监听基础操作触发事件 */
    onBeforeBaseOperate(fn: (layer: ILayerProps, operateType: BaseLayerViewType) => void, weight = 0) {
        return this.service.events.on(beforeBaseOperateEventName, fn, weight);
    }
    __emitBeforeBaseOperate(layer: ILayerProps, operateType: BaseLayerViewType) {
        this.service.events.emit(beforeBaseOperateEventName, layer, operateType);
    }

    /**
     * 允许宿主在移动结算前接收拖拽；接收后由编辑器恢复位置并跳过编组和历史记录
     *
     * @param fn - 接收图层、视口坐标和取消本次移动的方法
     * @returns 事件订阅 ID
     */
    onBeforeLayerMoveEnd(fn: (layerId: string, point: IPoint, cancel: () => void) => void) {
        return this.service.events.on(beforeLayerMoveEndEventName, fn);
    }

    /**
     * 通知宿主处理移动释放，返回是否需要恢复本次移动
     *
     * @param layerId - 移动的图层 ID
     * @param point - 释放时的视口坐标
     * @returns 是否被宿主接收
     */
    __emitBeforeLayerMoveEnd(layerId: string, point: IPoint): boolean {
        let cancelled = false;
        this.service.events.emit(beforeLayerMoveEndEventName, layerId, point, () => {
            cancelled = true;
        });
        return cancelled;
    }

    /** 监听基础操作事件 */
    onBaseOperate(
        fn: (layerId: string, operateType: BaseLayerViewType, args: IBaseOperateFlag, data?: IBaseOperateData) => void,
        weight = 0,
    ) {
        return this.service.events.on(baseOperateEventName, fn, weight);
    }
    /**
     * 发送基础操作事件
     */
    __emitBaseOperate(
        layerId: string,
        operateType: BaseLayerViewType,
        args: IBaseOperateFlag,
        data?: IBaseOperateData,
    ) {
        if (operateType === BaseLayerViewType.MOVE || operateType === BaseLayerViewType.ROTATE) {
            this.__emitLayerMoveOrRotate(layerId, operateType, args, data);
        }
        this.service.events.emit(baseOperateEventName, layerId, operateType, args, data);
    }

    /**
     * 监听所有舞台重新渲染事件
     * @param fn - 事件处理函数
     * @param weight - 事件权重
     * @returns
     */
    onRerenderAll(fn: (renderFrameIds: number[], diff: number[]) => void, weight = 0) {
        return this.service.events.on(rerenderAllFramesEventName, fn, weight);
    }
    __emitRerenderAll(diff: number[]) {
        this.service.events.emit(rerenderAllFramesEventName, this.__renderFrameIdsList, diff);
    }

    /** 监听舞台布局完成事件 */
    onFrameLayoutComplete(fn: (renderFrameIds: number[]) => void, weight = 0) {
        return this.service.events.on(frameLayoutCompleteEventName, fn, weight);
    }
    __emitFrameLayoutComplete(renderFrameIds: number[]) {
        this.service.events.emit(frameLayoutCompleteEventName, renderFrameIds);
    }

    // /**
    //  * 监听单个舞台重新渲染事件
    //  * @param fn - 事件处理函数
    //  * @param weight - 事件权重
    //  * @returns
    //  */
    // onRerender(fn: (frameId: number) => void, weight = 0) {
    //     return this.service.events.on(rerenderFrameEventName, fn, weight);
    // }
    // __emitRerender(frameId: number) {
    //     this.service.events.emit(rerenderFrameEventName, frameId);
    // }

    /** 尝试获取裁剪区 */
    get(frameId: number): EdgelessFrame | null;
    get(layerId: string): EdgelessFrame | null;
    get(layerProps: ILayerProps): EdgelessFrame | null;
    get(args: number | string | ILayerProps) {
        if (isNumber(args)) {
            return this.__frames.get(args) ?? null;
        }
        let layer: ILayerProps | null = null;
        if (isString(args)) {
            layer = this.service.flatLayer.getLayer(args);
        }
        if (isLayerProps(args)) {
            layer = args;
        }
        if (!layer) return null;
        const originIndex = layer.__originIndex!;
        return this.__frames.get(originIndex) ?? null;
    }

    private __setFrames(frameIds: number[]) {
        const oldFrameIds = Array.from(this.__frames.keys());
        frameIds.forEach((frameId) => {
            if (!this.__frames.has(frameId)) {
                const bgLayer = this.getBackgroundLayer(frameId);
                if (bgLayer) {
                    this.__frames.set(frameId, new EdgelessFrame(frameId, this.service));
                }
            } else {
                this.__frames.get(frameId)?.update();
            }
        });
        const diff = difference(oldFrameIds, frameIds);
        diff.forEach((frameId) => {
            this.__frames.delete(frameId);
        });
        // console.log('setFrames', this.__frames.keys());
    }

    /** 获取 frame 数量 */
    get size() {
        return this.__frames.size;
    }

    /** 获取裁剪区索引 */
    pick(layer: ILayerProps): number | null;
    pick(shape: IShape): number | null;
    pick(point: XY): number | null;
    pick(args: XY | IShape | ILayerProps): number | null {
        const { edgelessCanvas } = this.service;
        // 单画布模式下，不进行拾取
        if (!edgelessCanvas.isEdgelessMode) {
            return null;
        }

        let info: ISize & IPosition & { rotate: number };
        if (isLayerProps(args)) {
            info = args;
        } else if (isNumber(args.x) && isNumber(args.y)) {
            info = { ...getPointRect(args as XY), rotate: 0 };
        } else {
            info = { ...(args as IShape), rotate: 0 };
        }
        const renderFrameIds = this.getRenderFrameIds();

        const layers: ILayerProps[] = [];
        for (let i = 0; i < renderFrameIds.length; i++) {
            const originIndex = renderFrameIds[i];
            if (this.get(originIndex)) {
                layers.push(this.getShape(originIndex) as ILayerProps);
            }
        }
        const frameId = this.service.transform.tryPickWrap(info, layers);
        return frameId ? Number(frameId) : null;
    }

    /** 根据点获取图层id */
    pickWithLayerId(point: XY) {
        const frameId = this.pick(point);
        let layerId = '';
        if (isNumber(frameId)) {
            const pointRect = getPointRect(point);
            const layers = this.getCollisionLayers(frameId, false);
            layerId = this.service.transform.tryPickWrap(pointRect, layers);
        }
        return {
            layerId,
            frameId,
        };
    }

    /** 检查点是否在图层内 */
    checkPointInLayer(point: XY, layerId: string) {
        const layer = this.service.flatLayer.getLayer(layerId);
        if (!layer) return false;
        const pointRect = getPointRect(point);
        return this.service.transform.tryPickWrap(pointRect, [layer]);
    }

    /** 获取基础信息 */
    getBaseInfo(frameId: number): TBBox {
        const size = this.getSize(frameId);
        const { left, top } = this.getBackgroundLayer(frameId) || {
            left: 0,
            top: 0,
        };
        const offsetTop = this.getFrameTransform(frameId).offsetTop;
        return {
            ...size,
            left,
            top: top + offsetTop,
        };
    }

    /** 获取形状 */
    getShape(frameId: number): IShape {
        const info = this.getBaseInfo(frameId);
        return {
            id: `${frameId}`,
            left: info.left,
            top: info.top,
            width: info.width,
            height: info.height,
            rotate: 0,
        };
    }

    /** 交换到指定画布 */
    swapToFrame(layerId: string, inFrameId: number) {
        const outFrameId = this.getLayerFrameId(layerId);
        if (!isNumber(outFrameId)) return null;

        const layer = this.service.flatLayer.getLayer(layerId);
        const renderIds = this.getLayerIds(outFrameId);
        const isExtraGroup = this.service.flatLayer.checkIsExtraGroup(layer);
        const childIds = isExtraGroup ? layer?.children?.map((item) => item.id) : [];
        const childIndexes = childIds.map((id) => renderIds.indexOf(id));
        const layerIndex = renderIds.indexOf(layerId);
        const delIds = [...childIds, layerId];
        pullAll(renderIds, delIds);

        return {
            isExtraGroup,
            childIds,
            childIndexes,
            layerId,
            layerIndex,
            outFrameId,
            inFrameId,
        };
    }

    /** 恢复交换到指定画布数据 */
    restoreStickyInfo(info: {
        isExtraGroup: boolean;
        childIds: string[];
        childIndexes: number[];
        layerId: string;
        layerIndex: number;
        outFrameId: number;
        inFrameId: number;
        isRedo?: boolean;
        isUndo?: boolean;
        layer: ILayerProps;
    }) {
        const {
            isRedo,
            isUndo,
            layer,
            outFrameId,
            inFrameId,
            childIndexes,
            isExtraGroup,
            layerId,
            layerIndex,
            childIds,
        } = info;
        const delIds = [...childIds, layerId];

        const inRenderIds = this.getLayerIds(inFrameId);
        pullAll(inRenderIds, delIds);

        const outRenderIds = this.getLayerIds(outFrameId);
        pullAll(outRenderIds, delIds);

        this.service.edgelessCanvas.addDirtyFrameId(outFrameId);
        this.service.edgelessCanvas.addDirtyFrameId(inFrameId);

        if (isRedo) {
            this.addOrUpdateLayer(layer, inFrameId);
        }
        if (isUndo) {
            if (isExtraGroup) {
                for (let i = 0; i < childIds.length; i++) {
                    const id = childIds[i];
                    const index = childIndexes[i];
                    outRenderIds.splice(index, 0, id);
                }
            }
            outRenderIds.splice(layerIndex, 0, layerId);
            this.addOrUpdateLayer(layer, outFrameId);
        }
    }

    /** 遍历粘性移入图层 */
    traverseStickyIn(frameId: number, fn?: (layerId: string) => void) {
        if (this.__stickyInfo?.in === frameId) {
            this.service.flatLayer.traverseIds(false, [this.__stickyInfo.layerId], (id: string) => {
                fn?.(id);
            });
        }
    }

    /** 获取粘性移出图层 ID 列表 */
    getStickyOutFlatIds(frameId: number) {
        const ids = new Set<string>();
        if (this.__stickyInfo?.out === frameId) {
            this.service.flatLayer.traverseIds(false, [this.__stickyInfo.layerId], (id: string) => {
                ids.add(id);
            });
        }
        return ids;
    }

    /** 检查对象在裁剪区内是否可见 */
    checkIsVisible(layerId: string, frameId: number) {
        const { flatLayer, transform } = this.service;
        const layer = flatLayer.getLayer(layerId);
        if (!layer) return false;
        // if (isNumber(frameId)) {
        const frame = this.get(frameId);
        if (!frame) return false;
        // 主图宽高表示裁剪后的舞台尺寸，碰撞检测需要使用偏移后的完整图片尺寸。
        const renderLayer = this.service.behavior.get(layer)?.isMainLayer?.()
            ? { ...layer, ...getMainLayerRenderSize(layer) }
            : layer;
        const res = transform.tryPickWrap(renderLayer, [this.getShape(frame.frameId) as ILayerProps]);
        return !!res;
        // } else {
        //     const frame = this.get(layer.__originIndex!);
        //     return !!frame;
        // }
    }

    /**
     * 检查对象是否在 frame 内
     * 无限画布模式下，frameId=0 的为第一个无限大画布，其他为 frame 画布
     */
    checkInFrame(layerId: string) {
        const { edgelessCanvas } = this.service;
        if (!edgelessCanvas.isEdgelessMode) return true;
        const frameId = this.getLayerFrameId(layerId);
        return !!frameId;
    }

    /** 检查图层是否允许吸附到 frame */
    checkCanStickyToFrame(layerId: string) {
        const wrapLayer = this.service.flatLayer.getWrapLayer(layerId);
        if (!wrapLayer) return false;

        let disableStickyToFrame = false;
        traverseTree(wrapLayer, (item) => {
            if (!disableStickyToFrame) {
                disableStickyToFrame = this.service.behavior.get(item)?.disableStickyToFrame?.(item) ?? false;
            }
        });

        return !disableStickyToFrame;
    }

    /**
     * 查找图层
     */
    findLayer(layerId: string) {
        const { flatLayer } = this.service;
        const layer = flatLayer.getLayer(layerId);
        if (layer) return layer;
        return null;
    }

    /** 创建粘性图层 */
    createSticky(layerId: string) {
        if (!this.service.edgelessCanvas.isEdgelessMode) return null;

        const wrapLayer = this.service.flatLayer.getWrapLayer(layerId);
        const layer = this.service.flatLayer.getLayer(layerId);
        if (!wrapLayer || !layer) return null;

        if (!this.checkCanStickyToFrame(layerId)) return null;

        // 移动子图层，不需要判断粘性
        if (wrapLayer.id !== layer.id) return null;

        const outFrameId = this.getLayerFrameId(layerId);
        if (!isNumber(outFrameId)) return null;

        let stickyFrameId = layer.__stickyFrameId;
        layer.__preStickyFrameId = stickyFrameId;

        if (isNumber(stickyFrameId)) {
            const stickyFrame = this.get(stickyFrameId);
            if (!stickyFrame || (stickyFrame && !this.checkIsVisible(layerId, stickyFrameId))) {
                layer.__stickyFrameId = undefined;
                stickyFrameId = undefined;
                this.__stickyInfo = null;
            }
        }

        if (!isNumber(stickyFrameId)) {
            if (this.get(outFrameId)) {
                if (!this.checkIsVisible(layerId, outFrameId)) {
                    const inFrameId = this.pick(layer);
                    if (isNumber(inFrameId) && inFrameId !== outFrameId) {
                        layer.__stickyFrameId = inFrameId;
                        this.__stickyInfo = {
                            in: inFrameId,
                            out: outFrameId,
                            layerId,
                        };
                    } else {
                        layer.__stickyFrameId = 0;
                        this.__stickyInfo = {
                            in: 0,
                            out: outFrameId,
                            layerId,
                        };
                    }
                }
            } else {
                const inFrameId = this.pick(layer);
                if (isNumber(inFrameId) && inFrameId !== outFrameId) {
                    layer.__stickyFrameId = inFrameId;
                    this.__stickyInfo = {
                        in: inFrameId,
                        out: outFrameId,
                        layerId,
                    };
                }
            }
        }

        return this.__stickyInfo;
    }

    /** 清除粘性图层 */
    clearSticky() {
        if (this.__stickyInfo) {
            const layer = this.service.flatLayer.getLayer(this.__stickyInfo.layerId);
            if (layer) {
                layer.__stickyFrameId = undefined;
                layer.__preStickyFrameId = undefined;
            }
        }
        this.__stickyInfo = null;
    }

    /** 监听图层排序事件 */
    onSortLayer(fn: (frameId: number) => void, weight = 0) {
        return this.service.events.on(sortLayerEventName, fn, weight);
    }

    /** 触发排序事件 */
    emitSortLayer(frameId: number) {
        this.service.events.emit(sortLayerEventName, frameId);
    }

    // /** 重新渲染舞台 */
    // rerender(frameId: number) {
    //     this.__emitRerender(frameId);
    // }

    /** 清除选中图层 */
    clearSelected() {
        this.setSelectedIds(new Set());
    }

    /** 设置选中图层 */
    setSelected(args: string[] | string) {
        const isArray = Array.isArray(args);
        const idsSet = new Set<string>();
        if (isArray) {
            args.forEach((id) => {
                idsSet.add(id);
            });
        } else {
            idsSet.add(args);
        }
        this.setSelectedIds(idsSet);
    }

    /** 设置悬浮图层 */
    setHoverLayer(hoverId: string) {
        const { editor } = this.service;
        if (editor.disableOperate) return;
        if (editor.disableHover) return;
        if (editor.isBaseOperate && hoverId) return;
        this.setHoverId(hoverId);
    }

    /** 删除悬浮图层 */
    delHoverId() {
        this.setHoverId('');
    }

    /** 尝试删除激活图层 */
    tryRemoveActiveLayer(frameIdOrDelLayerId: number | string) {
        const info = this.getActiveInfo();
        if (!info) return;
        if (isNumber(frameIdOrDelLayerId)) {
            const frameId = this.getLayerFrameId(info.info.id);
            if (frameId === frameIdOrDelLayerId) {
                this.delActiveLayer();
            }
        } else {
            const wrapLayer = this.service.flatLayer.getWrapLayer(frameIdOrDelLayerId, true);
            if (info.info.id === wrapLayer?.id) {
                this.delActiveLayer();
            }
        }
    }
    /** 尝试刷新激活图层 */
    tryRefreshActiveLayer(layerId: string) {
        const activeInfo = this.getActiveInfo();
        if (activeInfo) {
            const wrapLayer = this.service.flatLayer.getWrapLayer(layerId, true);
            if (wrapLayer?.id === activeInfo.info.id) {
                if (this.service.flatLayer.checkIsExtraGroup(wrapLayer)) {
                    this.setActiveLayer(activeInfo.info.id, true);
                } else {
                    this.setActiveLayer(activeInfo.info.pickId || activeInfo.info.id, true);
                }
            } else {
                const activeId = activeInfo.info.pickId || activeInfo.info.id;
                if (activeId === layerId) {
                    this.setActiveLayer(activeId, true);
                }
            }
        }
    }

    /** 设置激活图层 */
    setActiveLayer(layerId: string, ignoreDisableOperate = false) {
        const { editor } = this.service;
        if (editor.disableOperate && !ignoreDisableOperate) return;
        if (editor.disableActive) return;

        // console.log('setActiveLayer ===>', layerId);

        const layer = this.service.flatLayer.getLayer(layerId);
        if (!layer || this.service.behavior.get(layer)?.checkDisableActive?.(layer)) {
            this.setActiveInfo(null);
            return;
        }

        const warpLayer = this.service.flatLayer.getWrapLayer(layerId);
        let activeLayerId = warpLayer?.id || '';

        if (this.activeInfo?.info.id && (layerId !== warpLayer?.id || warpLayer?.id !== this.activeInfo.info.id)) {
            const oldLayer = this.service.flatLayer.getLayer(this.activeInfo.info.id);
            if (oldLayer) {
                const isRemoveGroup = this.tryRemoveGroup(oldLayer);
                if (isRemoveGroup) {
                    activeLayerId = layerId;
                }
            }
        }

        const { originInfo, childOriginInfoMap } = this.exportLayerOriginInfo(activeLayerId);
        if (!originInfo) {
            this.setActiveInfo(null);
            return;
        }

        originInfo.pickId = activeLayerId !== layerId ? layerId : '';

        this.setActiveInfo({
            info: originInfo,
            childInfoMap: childOriginInfoMap,
        });
    }

    /** 获取激活图层的图层id列表 */
    getActiveLayerIds() {
        const layer = this.getActiveLayer();
        if (!layer) return [];
        const ids: string[] = [];
        traverseTree(layer, (item) => ids.push(item.id));
        return ids;
    }

    /** 获取激活图层 */
    getActiveLayer() {
        if (!this.activeInfo?.info.id) return null;
        return this.service.flatLayer.getLayer(this.activeInfo.info.id);
    }

    /**
     * 获取激活图层的选中图层，
     * 如果 force 为 true，没有选中图层时，会返回激活图层
     */
    getActivePickLayer(force = true) {
        if (!this.activeInfo?.info.id) return null;
        return this.service.flatLayer.getLayer(
            force ? this.activeInfo?.info.pickId || this.activeInfo.info.id : this.activeInfo?.info.pickId,
        );
    }

    /** 删除激活图层 */
    delActiveLayer() {
        // console.log('delActiveLayer ===>');
        const activeLayer = this.getActiveLayer();
        this.tryRemoveGroup(activeLayer);
        this.setActiveInfo(null);
    }

    /** 删除任意选中图层 */
    delLayer(layerId: string): IDelLayerData | null {
        const layer = this.service.flatLayer.getLayer(layerId, true);
        if (!layer || layer.lock) return null;
        const originIndex = this.getLayerFrameId(layerId);
        if (!isNumber(originIndex)) return null;
        const renderIds = this.getLayerIds(originIndex);
        const warpLayer = this.service.flatLayer.getWrapLayer(layerId);

        /** 向上递归删除只有0/1个元素的组 */
        const updateParentLayer = (layer: ILayerProps, layerId: string): ILayerProps => {
            if (layerId) {
                remove(layer.children || [], (item) => item.id === layerId);
                this.service.flatLayer.delete(layerId);
                pull(renderIds, layerId);
            }
            const childCount = layer.children?.length ?? 0;
            const parentId = layer.parentId;

            if (parentId) {
                const parentLayer = this.service.flatLayer.getLayer(parentId);

                if (childCount === 0) {
                    remove(parentLayer.children || [], (item) => item.id === layer.id);
                    this.service.flatLayer.delete(layer.id);
                    pull(renderIds, layer.id);
                    return updateParentLayer(parentLayer, '');
                } else if (childCount === 1) {
                    const childLayer = layer.children[0];
                    const layerIndex = (parentLayer.children || []).findIndex((item) => item.id === layer.id);
                    if (layerIndex !== -1) {
                        childLayer.parentId = parentLayer.id;
                        parentLayer.children[layerIndex] = childLayer;
                        this.service.flatLayer.delete(layer.id);
                        pull(renderIds, layer.id);
                        return updateParentLayer(parentLayer, '');
                    }
                }
            }

            return layer;
        };

        // 删除子图层
        if (warpLayer?.id !== layerId && layer.parentId) {
            this.tryRemoveGroup(this.service.flatLayer.getWrapLayer(layerId, true));
            const oldLayer = cloneDeep(warpLayer) as ILayerProps;
            const parentLayer = updateParentLayer(this.service.flatLayer.getLayer(layer.parentId), layerId);
            const parentIndex = renderIds.indexOf(parentLayer.id);
            const childCount = parentLayer.children?.length ?? 0;

            if (childCount === 0) {
                // 没有子图层，删除组
                const activeId = this.activeInfo?.info.id;
                if (activeId === parentLayer.id) {
                    this.setActiveInfo(null);
                }
                // const parentIndex = renderIds.indexOf(parentLayer.id);
                pull(renderIds, parentLayer.id);
                this.service.flatLayer.delete(parentLayer.id);
                return {
                    item: {
                        oldLayer,
                        oldLayerIndex: parentIndex,
                        replaceLayerId: '',
                        delId: layerId,
                        childIndexes: [],
                        childIds: [],
                        delAll: false,
                    },
                    operate: {
                        activeId: '',
                    },
                };
            } else if (childCount === 1) {
                // 剩一个，删除组，替换为子图层
                const childLayer = parentLayer.children[0];
                childLayer.parentId = undefined;
                // const parentIndex = renderIds.indexOf(parentLayer.id);
                renderIds.splice(parentIndex, 1, childLayer.id);
                const layer = cloneDeep(childLayer);
                this.service.flatLayer.delete(parentLayer.id);
                pull(renderIds, parentLayer.id);
                this.addOrUpdateLayer(layer, undefined, true);

                return {
                    item: {
                        oldLayer,
                        oldLayerIndex: parentIndex,
                        replaceLayerId: childLayer.id,
                        delId: layerId,
                        childIndexes: [],
                        childIds: [],
                        delAll: false,
                    },
                    operate: {
                        activeId: childLayer.id,
                    },
                };
            } else {
                // 剩多个子图层
                const newLayer = cloneDeep(parentLayer);
                this.addOrUpdateLayer(cloneDeep(parentLayer), undefined, true);

                return {
                    item: {
                        newLayer,
                        oldLayer,
                        oldLayerIndex: parentIndex,
                        replaceLayerId: '',
                        delId: layerId,
                        childIndexes: [],
                        childIds: [],
                        delAll: false,
                    },
                    operate: {
                        activeId: oldLayer?.id || '',
                    },
                };
            }
        } else if (warpLayer?.id === layerId) {
            // 删除最外层
            const activeId = this.activeInfo?.info.id;

            if (layerId === activeId) {
                this.setActiveInfo(null);
            }

            const isExtraGroup = this.service.flatLayer.checkIsExtraGroup(layer);
            const childIds = isExtraGroup ? layer?.children?.map((item) => item.id) : [];
            const childIndexes = childIds.map((id) => renderIds.indexOf(id));
            const layerIndex = renderIds.indexOf(layerId);

            const delIds = [...childIds, layerId];
            pullAll(renderIds, delIds);
            delIds.forEach((id) => {
                this.service.flatLayer.delete(id);
            });

            return {
                item: {
                    oldLayer: layer,
                    oldLayerIndex: layerIndex,
                    replaceLayerId: '',
                    delId: layerId,
                    childIndexes,
                    childIds,
                    delAll: true,
                },
                operate: {
                    activeId: '',
                },
            };
        }
        return null;
    }

    /** 添加或更新图层 */
    addOrUpdateLayer(layer: ILayerProps, destOriginIndex?: number, onlyUpdateBaseInfo?: boolean) {
        const behaviorService = this.service.behavior;
        const flatLayer = this.service.flatLayer;
        let isAgentLayer = false;

        traverseTree(layer, (item) => {
            if (item.extraInfo?.agent) {
                isAgentLayer = true;
            }
        });

        if (!isNumber(destOriginIndex)) {
            const curFrameId = this.getLayerFrameId(layer.id);
            if (!isNumber(curFrameId)) {
                const activeOriginIndex = this.getActiveFrameId();
                if (this.service.edgelessCanvas.isEdgelessMode) {
                    const frameId = isAgentLayer ? null : this.pick(layer);
                    if (isNumber(frameId)) {
                        // console.log('addOrUpdateLayer 1');
                        destOriginIndex = frameId;
                    } else {
                        // console.log('addOrUpdateLayer 2');
                        destOriginIndex = isAgentLayer
                            ? (this.getRenderFrameIds()[0] ?? activeOriginIndex)
                            : activeOriginIndex;
                    }
                } else {
                    // console.log('addOrUpdateLayer 3');
                    destOriginIndex = activeOriginIndex;
                }
            } else {
                // console.log('addOrUpdateLayer 4');
                destOriginIndex = curFrameId;
            }
        }

        const info = this.getFrameIdAndLayerIds(destOriginIndex, true);
        const originIds = info.layerIds;
        const originIndex = info.frameId;
        const isNewOriginIndex = !this.checkFrameIsExist(originIndex);

        const hasId = (id: string) => {
            return indexOf(originIds, id) !== -1;
        };
        const pushRenderId = (id: string) => {
            if (!hasId(id)) {
                originIds.push(id);
            }
        };
        const updateLayer = (layer: ILayerProps, isRoot: boolean) => {
            const behavior = behaviorService.get(layer);
            behavior?.onBeforeAddOrUpdate?.(layer);
            if (behavior && !behavior.getEdgelessObject) {
                console.log('warning: ', layer.layerType, '没有实现 getEdgelessObject 方法');
            }
            layer.__originIndex = originIndex;
            (layer.children || []).forEach((item) => {
                updateLayer(item, false);
            });
            if (isRoot && !layer.parentId) {
                if (flatLayer.checkIsExtraGroup(layer)) {
                    (layer.children || []).forEach((item) => {
                        pushRenderId(item.id);
                    });
                }
                pushRenderId(layer.id);
            }
            convertMaterialIdsToMap(layer);
            flatLayer.set(layer.id, layer, {
                updateDependencies: isRoot,
                onlyUpdateBaseInfo,
            });
        };

        updateLayer(layer, true);

        if (isNewOriginIndex && !this.checkFrameIsRender(originIndex)) {
            this.addFrameIds(originIndex);
        }

        return layer;
    }

    /** 批量更新图层 */
    batchUpdateLayer(layers: ILayerProps[], skipChild = false) {
        const flatLayer = this.service.flatLayer;

        const updateLayer = (layer: ILayerProps) => {
            const newLayer = { ...layer };
            if (!skipChild) {
                (newLayer.children || []).forEach((item) => {
                    updateLayer(item);
                });
            }
            flatLayer.set(layer.id, newLayer, {
                updateDependencies: !newLayer.parentId,
            });
        };

        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            updateLayer(layer);
        }
    }

    /** 标记图层替换尚未完成，供排版与绘制跳过中间状态 */
    get isReplacingLayer() {
        return this.__replaceLayerDepth > 0;
    }

    /**
     * 订阅完整图层树及渲染顺序恢复后的替换通知
     * @param fn - 接收本轮涉及的 frame ID 集合
     * @returns 事件订阅句柄
     */
    onLayersReplaced(fn: (frameIds: ReadonlySet<number>) => void) {
        return this.service.events.on(layersReplacedEventName, fn);
    }

    /**
     * 替换图层，并在最外层操作完成后通知排版
     *
     * 副作用：更新图层树、渲染顺序并触发替换完成事件。
     *
     * @param oldIds - 同一级待替换图层 ID
     * @param newLayers - 替换后的图层树
     * @param restoreData - 撤销时恢复的图层及原索引
     * @returns 替换结果；无效操作或历史恢复时返回 null
     */
    replaceLayer(
        oldIds: string[] | string,
        newLayers?: ILayerProps[] | ILayerProps,
        restoreData?: { index: number; layer: ILayerProps }[],
    ): IReplaceLayerResult | null {
        this.__replaceLayerDepth++;
        let completed = false;
        try {
            const result = this.__replaceLayer(oldIds, newLayers, restoreData);
            completed = true;
            return result;
        } finally {
            this.__replaceLayerDepth--;
            if (!this.isReplacingLayer) {
                const frameIds = this.__replacedFrameIds;
                this.__replacedFrameIds = new Set();
                if (completed && frameIds.size) {
                    this.service.events.emit(layersReplacedEventName, frameIds);
                }
            }
        }
    }

    private __replaceLayer(
        oldIds: string[] | string,
        newLayers?: ILayerProps[] | ILayerProps,
        restoreData?: { index: number; layer: ILayerProps }[],
    ): IReplaceLayerResult | null {
        const flatLayer = this.service.flatLayer;

        oldIds = isArray(oldIds) ? oldIds : [oldIds];
        newLayers = isArray(newLayers) ? newLayers : newLayers ? [newLayers] : undefined;

        const originIndex = this.getLayerFrameId(oldIds[0]);
        if (!isNumber(originIndex) || (!newLayers && !restoreData)) {
            return null;
        }
        // 替换操作，判断数据是否合法
        // oldIds 图层必须属于同一级
        const groupId = flatLayer.getLayer(oldIds[0])?.parentId;
        for (let i = 0; i < oldIds.length; i++) {
            const id = oldIds[i];
            const layer: ILayerProps = flatLayer.getLayer(id);
            if (!layer) return null;
            // 如果 rootId 存在，则 layer.parentId 必须存在且等于 rootId
            if (groupId) {
                if (layer.parentId !== groupId) return null;
            } else {
                if (layer.parentId) return null;
            }
        }

        this.__replacedFrameIds.add(originIndex);
        const originSortIds = groupId
            ? (flatLayer.getLayer(groupId).children || []).map((v) => v.id)
            : this.getLayerIds(originIndex);
        const delInfo: { index: number; layer: ILayerProps }[] = [];
        const collectIndex = (id: string, isRoot = false) => {
            const layer: ILayerProps = flatLayer.getLayer(id, true);
            if (layer) {
                (layer.children || []).forEach((item) => {
                    collectIndex(item.id);
                });
                if (isRoot) {
                    const index = originSortIds.indexOf(layer.id);
                    delInfo.push({
                        index,
                        layer,
                    });
                }
                flatLayer.delete(layer.id);
            }
        };

        if (newLayers) {
            traverseTrees(newLayers, (layer) => {
                layer.__originIndex = originIndex;
            });
        }
        // 收集待删除的索引信息
        for (let i = 0; i < oldIds.length; i++) {
            collectIndex(oldIds[i], true);
        }
        if (!delInfo.length) {
            return null;
        }
        const sortedDelInfo = delInfo.sort((a, b) => {
            return a.index - b.index;
        });
        // 执行替换操作
        if (newLayers) {
            if (!groupId) {
                // 删除一级图层，需要剔除渲染id
                pullAll(
                    originSortIds,
                    sortedDelInfo.map((item) => item.layer.id),
                );
                const minIndex = delInfo[0].index;
                const originIds = this.getLayerIds(originIndex);
                const newIds = insertToArrByIndex(
                    cloneDeep(originIds),
                    minIndex,
                    newLayers.map((item) => item.id),
                );
                originIds.length = 0;
                originIds.push(...newIds);
                for (let i = 0; i < newLayers.length; i++) {
                    this.addOrUpdateLayer(newLayers[i], originIndex);
                }
            } else {
                const oldLayer = flatLayer.getLayer(groupId, true);
                const newChild = cloneDeep(
                    (oldLayer.children = oldLayer.children.filter((v) => oldIds.indexOf(v.id) === -1)),
                );
                const minIndex = delInfo[0].index;
                for (let i = 0; i < newLayers.length; i++) {
                    const layer = newLayers[i];
                    layer.parentId = groupId;
                }
                oldLayer.children.length = 0;
                oldLayer.children.push(...insertToArrByIndex(newChild, minIndex, newLayers));
                this.addOrUpdateLayer(oldLayer, originIndex);
            }

            return {
                activeId: oldIds.length === 1 ? oldIds[0] : undefined,
                wrapId: groupId ? flatLayer.getWrapLayer(groupId, false)?.id : undefined,
                newIds: newLayers.map((v) => v.id),
                oldInfo: delInfo,
            };
        } else if (restoreData) {
            // 历史记录恢复
            if (!groupId) {
                pullAll(
                    originSortIds,
                    sortedDelInfo.map((item) => item.layer.id),
                );
                const insetMap = new Map<number, string>();
                for (let i = 0; i < restoreData.length; i++) {
                    const item = restoreData[i];
                    insetMap.set(item.index, item.layer.id);
                }
                insertWithIndex(originSortIds, insetMap);
                for (let i = 0; i < restoreData.length; i++) {
                    const item = restoreData[i];
                    traverseTree(item.layer, (layer) => {
                        flatLayer.set(layer.id, layer, {
                            updateDependencies: false,
                        });
                    });
                    flatLayer.set(item.layer.id, item.layer, {
                        updateDependencies: !item.layer.parentId,
                    });
                }
            } else {
                const oldLayer = flatLayer.getLayer(groupId, true);
                const newChild = cloneDeep(
                    (oldLayer.children = oldLayer.children.filter((v) => oldIds.indexOf(v.id) === -1)),
                );
                const insetMap = new Map<number, string>();
                const newLayers: ILayerProps[] = [];
                for (let i = 0; i < restoreData.length; i++) {
                    const item = restoreData[i];
                    insetMap.set(item.index, item.layer.id);
                    newLayers.push(item.layer);
                    flatLayer.set(item.layer.id, item.layer, {
                        updateDependencies: !item.layer.parentId,
                    });
                }
                const minIndex = delInfo[0].index;
                oldLayer.children.length = 0;
                oldLayer.children.push(...insertToArrByIndex(newChild, minIndex, newLayers));
                this.addOrUpdateLayer(oldLayer, originIndex);
            }
        }

        return null;
    }

    /** 显示/隐藏素材层 */
    toggleMaterial(isHidden: boolean, disableHideFn?: (layer: ILayerProps) => boolean, frameId?: number) {
        const index = isNumber(frameId) ? frameId : this.getActiveFrameId();
        const layers = this.getLayers(index);
        layers.forEach((item) => {
            const disableCollision = disableHideFn
                ? disableHideFn(item)
                : this.service.behavior.get(item.layerType)?.checkDisableCollision?.(item);
            if (!disableCollision && item.__isHidden !== isHidden) {
                const layer = cloneDeep(item);
                traverseTree(layer, (item) => {
                    item.__isHidden = isHidden;
                    this.service.flatLayer.set(item.id, item, {
                        updateDependencies: false,
                        onlyUpdateBaseInfo: true,
                    });
                });
            }
        });
        this.service.edgelessCanvas.requestRenderAll();
    }

    /**
     * 导出图层的原始信息
     *
     * @param info - 图层 ID 或 图层数据
     * @param fetchChildOriginInfoCb - 可选；获取子图层原始信息回调方法
     */
    exportLayerOriginInfo(
        info: string | ILayerProps,
        fetchChildOriginInfoCb?: (originInfo: ILayerOriginInfo) => void,
    ): IExportLayerOriginInfo {
        const flatLayer = this.service.flatLayer;
        const isLayerId = isString(info);
        const layer = isLayerId ? flatLayer.getLayer(info) : info;
        const childOriginInfoMap = new Map<string, ILayerOriginInfo>();

        if (!layer) {
            return {
                originInfo: undefined,
                childOriginInfoMap,
            };
        }

        const getLayerOriginInfo = (args: string | ILayerProps) => {
            const originLayer = isString(args) ? flatLayer.getLayer(args) : args;
            if (!originLayer) {
                return;
            }
            const { id, left, top, rotate, width, height, layerType, lock } = originLayer;
            const originInfo = { id, left, top, rotate, width, height, layerType, lock };
            const baseInfo = this.service.behavior.get(originLayer)?.getActiveBaseInfo?.(originLayer);
            return Object.assign(
                originInfo,
                {
                    pickId: '',
                    cutWidth: LAYER_MIN_SIZE,
                    cutHeight: LAYER_MIN_SIZE,
                },
                baseInfo,
            ) as ILayerOriginInfo;
        };

        const cloneInfo = (layer: ILayerProps) => {
            cloneChildren(layer.children);
            const originInfo = getLayerOriginInfo(isLayerId ? layer.id : layer);
            if (!originInfo) return;
            childOriginInfoMap.set(originInfo.id, originInfo);
            fetchChildOriginInfoCb?.(originInfo);
        };

        const cloneChildren = (children: ILayerProps[]) => {
            (children || []).forEach((item) => {
                cloneInfo(item);
            });
        };

        const originInfo = getLayerOriginInfo(isLayerId ? layer.id : layer);

        cloneChildren(layer.children);

        return {
            originInfo,
            childOriginInfoMap,
        };
    }

    /**
     * 缩放图层
     *
     * @param layer - 图层属性
     * @param offsetWidth - 偏移宽度
     * @param direction - 可选；方向；默认为正向
     * @param disableWithGroupHasMinLayer - 可选；组内有最小元素，是否停止缩放；默认值为 `true`
     * @param isFromAdjustScale - 可选；操作是否来自智能缩放
     * @param cb - 可选；回调处理方法
     * @param isStart - 可选；如果是连续的缩放, 比如拖拽缩放, 就需要传入是否是第一次
     */
    scaleLayer(
        layer: ILayerProps,
        offsetWidth: number,
        direction = DirectionType.POSITIVE,
        disableWithGroupHasMinLayer = true,
        isFromAdjustScale = false,
        callback?: (layer: ILayerProps) => void,
        isStart = false,
    ) {
        const { originInfo, childOriginInfoMap } = this.exportLayerOriginInfo(layer);
        if (!originInfo) return;

        const flatLayersMap: Map<string, ILayerProps> = new Map();
        const flatLayer = (layer: ILayerProps) => {
            flatLayersMap.set(layer.id, layer);
            (layer.children || []).forEach((item) => flatLayer(item));
        };
        flatLayer(layer);

        let scaleInfo = { offset: offsetWidth, isMin: false };
        if (disableWithGroupHasMinLayer) {
            scaleInfo = this.__getScaleInfo(flatLayersMap, originInfo, childOriginInfoMap, offsetWidth);
        }

        this.__execScale(
            layer,
            scaleInfo,
            originInfo,
            childOriginInfoMap,
            direction,
            false,
            isStart,
            isFromAdjustScale,
            callback,
        );
    }

    /**
     * 缩放图层组
     * 对应旧的 adjustLayerScale 方法
     *
     * @param layers - 图层列表
     * @param oldSize - 旧尺寸
     * @param newSize - 新尺寸
     * @param useHeightScale - 是否使用高度缩放
     * @param resetPosition - 是否重置位置
     * @param isFromAdjustScale - 是否来自智能缩放
     * @param callback - 可选；回调处理方法
     */
    scaleLayers(
        layers: ILayerProps[],
        oldSize: ISize,
        newSize: ISize,
        useHeightScale = false,
        resetPosition = true,
        isFromAdjustScale = false,
        callback?: (layer: ILayerProps) => void,
    ) {
        let offsetWidth = newSize.width - oldSize.width;
        let finalHeight = 0;
        if (useHeightScale && resetPosition) {
            offsetWidth = oldSize.width * ((newSize.height - oldSize.height) / oldSize.height);
        }
        layers.forEach((layer) => {
            if (!layer || layer.parentId) return;
            const isBg = this.service.flatLayer.checkIsBackground(layer);
            const isMain = this.service.flatLayer.checkIsMain(layer);
            if (isBg || isMain) {
                const { width, height } = layer;
                const ratio = width / height;
                layer.width = newSize.width;
                finalHeight = Math.ceil(newSize.width / ratio);
                if (resetPosition) {
                    layer.height = newSize.height;
                } else {
                    layer.height = finalHeight;
                }
                this.service.behavior.get(layer)?.adjustResize?.(layer, layer.width, layer.height);
                callback?.(layer);
            } else {
                const scaleGroupTempLayer = getGroupLayerProps({
                    width: oldSize.width,
                    height: oldSize.height,
                    children: [layer],
                });
                this.scaleLayer(scaleGroupTempLayer, offsetWidth, undefined, false, isFromAdjustScale, (layer) => {
                    if (scaleGroupTempLayer.id !== layer.id) {
                        if (resetPosition) {
                            if (useHeightScale) {
                                const offsetLeft = newSize.width - oldSize.width / (oldSize.height / newSize.height);
                                layer.left += ~~(offsetLeft / 2);
                            } else {
                                const offsetTop = newSize.height - finalHeight;
                                layer.top += ~~(offsetTop / 2);
                            }
                        }
                        callback?.(layer);
                    }
                });
            }
        });
    }

    /**
     * 旋转图层
     *
     * @param layer - 图层属性
     * @param angle - 旋转角度
     * @param callback - 可选；回调处理方法
     */
    rotateLayer(layer: ILayerProps, angle: number, callback?: (layer: ILayerProps) => void) {
        const { originInfo, childOriginInfoMap } = this.exportLayerOriginInfo(layer);
        if (!originInfo) return;
        this.__execRotate(layer, originInfo, childOriginInfoMap, angle, callback);
    }

    /**
     * 调整图层组
     * 对应旧的 adjustLayerResize 方法
     *
     * @param layers - 图层列表
     * @param oldSize - 旧尺寸
     * @param newSize - 新尺寸
     * @param resetPosition - 是否重置位置
     */
    resizeLayers(layers: ILayerProps[], oldSize: ISize, newSize: ISize, resetPosition = true) {
        const offsetWidth = newSize.width - oldSize.width;
        const offsetHeight = newSize.height - oldSize.height;
        layers.forEach((layer) => {
            if (this.service.flatLayer.checkIsBackground(layer) || this.service.flatLayer.checkIsMain(layer)) {
                const newHeight = resetPosition ? newSize.height : layer.height;
                this.service.behavior.get(layer)?.adjustResize?.(layer, newSize.width, newHeight);
            } else {
                if (layer.children) {
                    this.resizeLayers(layer.children, oldSize, newSize, resetPosition);
                }

                layer.left += ~~(offsetWidth / 2);
                if (resetPosition) {
                    layer.top += ~~(offsetHeight / 2);
                }
            }
        });
    }

    /**
     * 缩放图层
     */
    updateLayerScale(
        layerId: string,
        offsetX: number,
        offsetY: number,
        beforeScaleLayerSize: ISize,
        direction: DirectionType,
        reverse: boolean,
        originInfo: ILayerOriginInfo,
        childOriginInfoMap: Map<string, ILayerOriginInfo>,
        args: IBaseOperateFlag,
        keepRatio?: boolean,
        isUseWidthScale?: boolean,
    ) {
        const isStart = args.isStart;
        const layer = this.service.flatLayer.getLayer(layerId, true);
        const scaleInfo = {
            ...this.__getScaleInfo(this.service.flatLayer, originInfo, childOriginInfoMap, offsetX),
            originalOffsetX: offsetX,
            offsetY,
            beforeScaleLayerSize,
            keepRatio,
            isUseWidthScale,
        };

        let isOnlyUpdateBaseInfo = false;
        this.__execScale(
            layer,
            scaleInfo,
            originInfo,
            childOriginInfoMap,
            direction,
            reverse,
            isStart,
            false,
            (layer) => {
                const onlyUpdateBaseInfo = this.service.behavior.get(layer)?.scaleOnlyUpdateBaseInfo?.(layer) ?? false;
                if (!isOnlyUpdateBaseInfo) {
                    isOnlyUpdateBaseInfo = onlyUpdateBaseInfo;
                }
                // console.log('updateLayerScale =>', layer.id, onlyUpdateBaseInfo);
                this.service.flatLayer.set(layer.id, layer, {
                    updateDependencies: layer.id === layerId,
                    onlyUpdateBaseInfo: args.isEnd ? false : onlyUpdateBaseInfo,
                });
            },
        );

        if (isOnlyUpdateBaseInfo) {
            this.service.edgelessCanvas.renderAll();
        }

        this.__emitBaseOperate(layerId, BaseLayerViewType.SCALE, args, {
            layerId,
            offsetX,
            offsetY,
            direction,
            reverse,
            keepRatio,
            beforeScaleLayerSize,
        });

        return {
            width: layer.width,
            height: layer.height,
            isMin: scaleInfo.isMin,
        };
    }

    /**
     * 更新图层尺寸
     */
    updateLayerResize(
        layerId: string,
        offset: number,
        direction: DirectionType,
        reverse: boolean,
        isChangeHeight: boolean,
        originInfo: ILayerOriginInfo,
        childOriginInfoMap: Map<string, ILayerOriginInfo>,
        args: IBaseOperateFlag,
    ) {
        const isStart = args.isStart;
        const layer = this.service.flatLayer.getLayer(layerId, true);
        const previousTickLayer = cloneDeep(layer);
        const resizeLayerIds: string[] = [];
        let isOnlyUpdateBaseInfo = false;
        let isMin = false;

        // 统一处理图层尺寸和位置
        const processLayer = (layer: ILayerProps, originInfo: ILayerOriginInfo, offsetChange: number) => {
            const { width: originWidth, height: originHeight } = originInfo;

            const minWidth = originInfo.cutWidth;
            const minHeight = originInfo.cutHeight;

            // 根据实际改变值调整尺寸
            if (isChangeHeight) {
                layer.height = Math.max(minHeight, Math.ceil(originHeight + offsetChange));
            } else {
                layer.width = Math.max(minWidth, Math.ceil(originWidth + offsetChange));
            }

            const res = this.service.behavior.get(layer)?.onBeforeResizeUpdate?.(layer, {
                isChangeHeight,
                previousTickLayer,
                isStart,
                originInfo,
                reverse,
            });

            if (res?.isMin && !isMin) {
                isMin = true;
            }

            // 计算新的位置
            const finalLayer = res || layer;
            const newCenter = isChangeHeight
                ? getNewCenterPoint(originInfo, finalLayer, reverse, 1)
                : getNewCenterPoint(originInfo, finalLayer, reverse, direction);

            layer.left = newCenter.x - finalLayer.width / 2;
            layer.top = newCenter.y - finalLayer.height / 2;

            resizeLayerIds.push(layer.id);

            const onlyUpdateBaseInfo = this.service.behavior.get(layer)?.resizeOnlyUpdateBaseInfo?.(layer) ?? false;
            if (!isOnlyUpdateBaseInfo) {
                isOnlyUpdateBaseInfo = onlyUpdateBaseInfo;
            }
            this.service.flatLayer.set(layer.id, layer, {
                updateDependencies: layer.id === layerId,
                onlyUpdateBaseInfo: args.isEnd ? false : onlyUpdateBaseInfo,
            });

            if (!res?.skipChild) {
                // 处理子图层，传递实际的尺寸变化值
                const { children } = layer;
                (children || []).forEach((item) => {
                    const value = childOriginInfoMap.get(item.id);
                    if (!value) return;
                    processLayer(item, value, offsetChange);
                });
            }
        };

        // 执行处理
        processLayer(layer, originInfo, offset);

        if (!isMin) {
            if (isChangeHeight) {
                isMin = layer.height <= originInfo.cutHeight;
            } else {
                isMin = layer.width <= originInfo.cutWidth;
            }
        }

        if (isOnlyUpdateBaseInfo) {
            this.service.edgelessCanvas.renderAll();
        }

        this.__emitBaseOperate(layerId, BaseLayerViewType.RESIZE, args, {
            layerId,
            offset,
            direction,
            reverse,
            isChangeHeight,
        });
        return { isMin, width: layer.width, height: layer.height };
    }

    /**
     * 旋转图层
     */
    updateLayerRotate(
        layerId: string,
        angle: number,
        originInfo: ILayerOriginInfo,
        childOriginInfoMap: Map<string, ILayerOriginInfo>,
        args: IBaseOperateFlag,
    ) {
        const rotate = 0;

        const layer = this.service.flatLayer.getLayer(layerId);
        if (!layer) return rotate;

        const rotateLayerIds: string[] = [];
        this.__execRotate(
            layer,
            originInfo,
            childOriginInfoMap,
            angle,
            (layer) => {
                rotateLayerIds.push(layer.id);
                this.service.flatLayer.set(
                    layer.id,
                    { ...layer },
                    {
                        updateDependencies: args.isEnd && layer.id === layerId,
                        onlyUpdateBaseInfo: true,
                    },
                );
            },
            args.enableAdsorb,
        );

        this.service.edgelessCanvas.renderAll();

        this.__emitBaseOperate(layerId, BaseLayerViewType.ROTATE, args, {
            layerId,
            angle,
        });

        return layer.rotate;
    }

    /**
     * 移动图层
     */
    updateLayerPosition(
        id: string,
        x: number,
        y: number,
        originInfo: ILayerOriginInfo,
        childOriginInfoMap: Map<string, ILayerOriginInfo>,
        args: IBaseOperateFlag,
    ) {
        const layer = this.service.flatLayer.getLayer(id);
        if (!layer) return { left: 0, top: 0 };

        const updatePosition = (layer: ILayerProps, originInfo: ILayerOriginInfo) => {
            const { children } = layer;
            (children || []).forEach((childLayer) => {
                const oldInfo = childOriginInfoMap.get(childLayer.id);
                if (!oldInfo) return;
                updatePosition(childLayer, oldInfo);
            });
            const { left, top } = originInfo;
            layer.left = ~~(left + x);
            layer.top = ~~(top + y);
            this.service.flatLayer.set(
                layer.id,
                { ...layer },
                {
                    updateDependencies: args.isEnd && layer.id === id,
                    onlyUpdateBaseInfo: true,
                },
            );
        };
        if (originInfo) {
            updatePosition(layer, originInfo);
        }

        this.service.edgelessCanvas.renderAll();

        this.__emitBaseOperate(id, BaseLayerViewType.MOVE, args, {
            layerId: id,
            x,
            y,
        });

        return {
            left: layer.left,
            top: layer.top,
        };
    }

    /**
     * 执行缩放操作
     *
     * @param layer - 图层属性
     * @param scaleInfo - 缩放信息
     * @param activeInfo - 激活的图层信息
     * @param activeMap - 激活图层信息映射对象
     * @param direction - 方向
     * @param reverse - 是否反转
     * @param isStart - 可选；是否开始
     * @param isFromAdjustScale - 可选；操作是否来自智能缩放
     * @param callback - 可选；回调处理方法
     */
    private __execScale(
        layer: ILayerProps,
        scaleInfo: ScaleInfo,
        activeInfo: ILayerOriginInfo,
        activeMap: Map<string, ILayerOriginInfo>,
        direction: DirectionType,
        reverse: boolean,
        isStart = false,
        isFromAdjustScale = false,
        callback?: (layer: ILayerProps) => void,
    ) {
        let widthRatio: number = 1;
        let heightRatio: number = 1;
        let parentOriginWidth: number = 0;
        let parentOriginHeight: number = 0;
        let parentOriginTop: number = 0;
        let parentOriginLeft: number = 0;
        let parentWidth: number = 0;
        let parentHeight: number = 0;
        let parentLeft: number = 0;
        let parentTop: number = 0;

        const updateScale = (
            layer: ILayerProps,
            originInfo: ILayerOriginInfo,
            previousTickLayer: ILayerProps,
            updateFlatLayer?: boolean,
        ) => {
            const { keepRatio = true, isUseWidthScale = true } = scaleInfo;
            const { children } = layer;
            const { width: originWidth, height: originHeight, left: originLeft, top: originTop } = originInfo;

            let width = 0;
            let height = 0;
            let left = 0;
            let top = 0;
            let correctResult: ICorrectLayerMinSize | undefined;
            if (!updateFlatLayer) {
                const minSize = this.__getLayerMinSize(originInfo, layer);
                const sizeController = this.__getScaleController(originInfo, minSize, scaleInfo);

                const newSize = keepRatio
                    ? isUseWidthScale
                        ? sizeController.useWidth()
                        : sizeController.useHeight()
                    : sizeController.useUnequalStretch();

                const customNextTickSize = this.service.behavior.get(layer)?.getNextTickScaleSize?.(scaleInfo);
                if (customNextTickSize) {
                    width = customNextTickSize.width;
                    height = customNextTickSize.height;
                } else {
                    width = newSize.width;
                    height = newSize.height;
                }

                correctResult = this.service.behavior
                    .get(layer)
                    ?.correctLayerMinSize?.(width, height, newSize.ratio, originInfo);
                if (correctResult) {
                    width = correctResult.width;
                    height = correctResult.height;
                    scaleInfo.isMin = correctResult.isMin;
                }

                if (direction === DirectionType.CENTER) {
                    left = originLeft + (originWidth - width) / 2;
                    top = originTop + (originHeight - height) / 2;
                } else {
                    const newCenter = getNewCenterPoint(originInfo, { width, height }, reverse, direction);
                    left = newCenter.x - width / 2;
                    top = newCenter.y - height / 2;
                }

                widthRatio = width / originWidth;
                heightRatio = height / originHeight;
                parentLeft = left;
                parentTop = top;
                parentWidth = width;
                parentHeight = height;
                parentOriginWidth = originWidth;
                parentOriginHeight = originHeight;
                parentOriginLeft = originLeft;
                parentOriginTop = originTop;
            } else {
                width = Math.ceil(widthRatio * originWidth);
                height = Math.ceil(heightRatio * originHeight);
                correctResult = this.service.behavior
                    .get(layer)
                    ?.correctLayerMinSize?.(width, height, widthRatio, originInfo);
                if (correctResult) {
                    width = correctResult.width;
                    height = correctResult.height;
                }
                left = ((originLeft - parentOriginLeft) / parentOriginWidth) * parentWidth + parentLeft;
                top = ((originTop - parentOriginTop) / parentOriginHeight) * parentHeight + parentTop;
            }

            layer.width = width;
            layer.height = height;
            layer.top = top;
            layer.left = left;

            this.service.behavior.get(layer)?.onBeforeScaleUpdate?.(layer, {
                previousTickLayer,
                originInfo,
                correctResult,
                isStart,
                isFromAdjustScale,
            });

            (children || []).forEach((item, index) => {
                const value = activeMap.get(item.id);
                const previousTickItemLayer = previousTickLayer.children[index];
                if (!value) return;
                updateScale(item, value, previousTickItemLayer, true);
            });

            callback?.(layer);
        };

        const previousTickLayer = cloneDeep(layer);
        updateScale(layer, activeInfo, previousTickLayer);
    }

    /**
     * 旋转图层
     *
     * @param layer - 图层属性
     * @param activeInfo - 激活的图层信息
     * @param activeMap - 激活图层信息映射对象
     * @param angle - 旋转角度
     * @param callback - 可选；回调处理方法
     * @param enableAdsorb - 可选；是否开启吸附；默认值为 `false`
     */
    __execRotate(
        layer: ILayerProps,
        activeInfo: ILayerOriginInfo,
        activeMap: Map<string, ILayerOriginInfo>,
        angle: number,
        callback?: (layer: ILayerProps) => void,
        enableAdsorb?: boolean,
    ) {
        const cPoint = getCenterPointForOrigin(activeInfo);
        const updateRotate = (layer: ILayerProps, originInfo: ILayerOriginInfo, newPoint?: IPoint) => {
            const { rotate, width, height } = originInfo;
            const hostKeyDown = isHotKeyPress();
            layer.rotate = getActualAngle(rotate + angle);
            const isAdsorb = Math.abs(layer.rotate % 90) < 2 || Math.abs(layer.rotate % 90) > 88;
            if (hostKeyDown) {
                layer.rotate = Math.round(layer.rotate / 15) * 15;
            } else if (enableAdsorb && isAdsorb) {
                layer.rotate = Math.round(layer.rotate / 90) * 90;
            }
            if (newPoint) {
                layer.left = newPoint.x - width / 2;
                layer.top = newPoint.y - height / 2;
            }

            callback?.(layer);

            const { children } = layer;
            (children || []).forEach((item) => {
                const value = activeMap.get(item.id);
                if (!value) return;
                const sPoint = getCenterPointForOrigin(value);
                const rPoint = rotatePointWithFocus(sPoint, cPoint, angle);
                updateRotate(item, value, rPoint);
            });
        };
        updateRotate(layer, activeInfo);
    }

    /**
     * 获取缩放控制器
     *
     * @param originInfo - 图层源信息
     * @param layerMinSize - 图层最小尺寸
     * @param scaleInfo - 缩放信息
     */
    private __getScaleController(
        originInfo: ILayerOriginInfo,
        layerMinSize: ISize,
        scaleInfo: { isMin: boolean; offset: number; offsetY?: number },
    ) {
        const layer = this.service.flatLayer.getLayer(originInfo.id);
        const { width: originWidth, height: originHeight, layerType } = originInfo;
        const { width: minWidth, height: minHeight } = layerMinSize;
        const needLimitSizeLayer = this.service.behavior.get(layer)?.getScaleIsNeedLimitSize?.();

        let ratio = 1;
        let width = 0;
        let height = 0;

        return {
            /**
             * 等比缩放
             * 根据宽度计算高度
             **/
            useWidth() {
                width = originWidth + scaleInfo.offset;
                if (needLimitSizeLayer) {
                    width = Math.max(width, minWidth);
                }
                ratio = width / originWidth;
                height = originHeight * ratio;
                if (needLimitSizeLayer) {
                    height = Math.max(height, minHeight);
                }

                if (!scaleInfo.isMin && layerType !== LayerType.GROUP_LAYER) {
                    scaleInfo.isMin = width <= minWidth || height <= minHeight;
                }

                if (scaleInfo.isMin) {
                    const ratioHeight = (height - originHeight) / originHeight;
                    scaleInfo.offset = ratioHeight * originWidth;
                    width = originWidth + scaleInfo.offset;
                }

                return { ratio, width: Math.ceil(width), height: Math.ceil(height) };
            },

            /**
             * 等比缩放
             * 根据高度计算宽度
             **/
            useHeight() {
                height = originHeight + (scaleInfo.offsetY || 0);
                if (needLimitSizeLayer) {
                    height = Math.max(height, minHeight);
                }
                ratio = height / originHeight;
                width = originWidth * ratio;
                if (needLimitSizeLayer) {
                    width = Math.max(width, minWidth);
                }
                if (!scaleInfo.isMin && layerType !== LayerType.GROUP_LAYER) {
                    scaleInfo.isMin = width <= minWidth || height <= minHeight;
                }

                if (scaleInfo.isMin) {
                    const ratioWidth = (width - originWidth) / originWidth;
                    scaleInfo.offsetY = ratioWidth * originHeight;
                    height = originHeight + scaleInfo.offsetY;
                }

                return { ratio, width: Math.ceil(width), height: Math.ceil(height) };
            },

            /**
             * 不等比缩放
             **/
            useUnequalStretch() {
                width = originWidth + scaleInfo.offset;
                height = originHeight + (scaleInfo.offsetY || 0);

                if (needLimitSizeLayer) {
                    width = Math.max(width, minWidth);
                    height = Math.max(height, minHeight);
                }

                ratio = width / originWidth; // 即使在不等比缩放中，仍然保留宽度比例用于后续计算

                return { ratio, width: Math.ceil(width), height: Math.ceil(height) };
            },
        };
    }

    /**
     * 调整缩放时的 offset
     *
     * @param flatLayers - 图层数据
     * @param layerOriginInfo - 图层源信息
     * @param childOriginInfoMap - 子图层源信息
     * @param offset - 偏移量
     */
    private __getScaleInfo(
        flatLayers: { get: (id: string) => ILayerProps | undefined },
        layerOriginInfo: ILayerOriginInfo,
        childOriginInfoMap: Map<string, ILayerOriginInfo>,
        offset: number,
    ) {
        // 是否达到组的最小值缩放值
        let isMin = false;
        if (offset < 0) {
            // 提取子图层到达临界尺寸的比例
            const ratioList: number[] = [];

            if (childOriginInfoMap.size) {
                childOriginInfoMap.forEach((value) => {
                    const layer = flatLayers.get(value.id);
                    if (layer) {
                        const ratio = this.service.behavior.get(layer)?.getScaleMinRatio?.(value, layer);
                        if (ratio !== undefined) {
                            ratioList.push(ratio);
                        }
                    }
                });

                // 调整偏移量
                if (ratioList.length) {
                    const { width: aWidth } = layerOriginInfo;
                    const maxRatio = Math.max(...ratioList);
                    const res = maxRatio * aWidth - aWidth;
                    offset = Math.max(res, offset);
                    isMin = res === offset;
                }
            }
        }

        return { offset, isMin };
    }

    /**
     * 获取图层的最小尺寸
     *
     * @param originInfo - 图层源信息
     * @param layer - 图层属性
     */
    private __getLayerMinSize(originInfo: ILayerOriginInfo, layer: ILayerProps) {
        const { cutWidth, cutHeight } = originInfo;
        const originLayer = this.service.flatLayer.getLayer(originInfo.id);
        const layerMinSize = this.service.behavior.get(originLayer)?.getLayerMinSize?.(originInfo, layer);

        return Object.assign(
            {
                width: cutWidth,
                height: cutHeight,
            },
            layerMinSize,
        );
    }

    /**
     * 检查图层是否需要渲染
     * @param layerId - 图层ID
     * @returns 是否需要渲染
     */
    checkLayerIsNeedRender(layerId: string) {
        const { flatLayer } = this.service;
        if (flatLayer.checkIsNeedHidden(layerId)) return false;
        const frameId = this.getLayerFrameId(layerId);
        if (isNumber(frameId)) {
            return this.__frameLayerIdsList[frameId]?.indexOf(layerId) !== -1;
        }
        return false;
    }

    /**
     * 检查舞台是否渲染
     * @param originIndex - 舞台原始索引
     */
    checkFrameIsRender(frameId: number) {
        return this.__renderFrameIdsList.indexOf(frameId) !== -1;
    }

    /**
     * 检查舞台是否存在
     * @param frameId - 舞台ID
     * @returns 是否存在
     */
    checkFrameIsExist(frameId: number) {
        return !!this.__frameLayerIdsList[frameId];
    }

    /** 渲染索引转舞台ID */
    indexToFrameId(index: number) {
        return this.__renderFrameIdsList[index] || 0;
    }

    /** 舞台ID转渲染索引 */
    frameIdToIndex(frameId: number) {
        const index = this.__renderFrameIdsList.indexOf(frameId);
        return index === -1 ? 0 : index;
    }

    /** 尝试移除临时组 */
    tryRemoveGroup(layer: ILayerProps | null) {
        const { flatLayer } = this.service;
        if (!layer || !flatLayer.checkIsExtraGroup(layer)) return;
        const frameId = this.getLayerFrameId(layer.id);
        if (!isNumber(frameId)) return;
        const layerIds = this.getLayerIds(frameId);
        if (layerIds.indexOf(layer.id) === -1) return;
        layer.children?.forEach((item) => {
            const layer = flatLayer.getLayer(item.id);
            if (layer) {
                layer.parentId = undefined;
            }
        });
        pull(layerIds, layer.id);
        flatLayer.delete(layer.id);
        return true;
    }

    /** 尝试创建临时组 */
    tryCreateGroup(frameId: number, groupId: string, childIdsSet: Set<string>) {
        const positions: IPoint[] = [];
        const children: ILayerProps[] = [];
        let newGroupId = '';
        let i = 0;
        const layerIds = this.getLayerIds(frameId);
        layerIds.forEach((id) => {
            if (childIdsSet.has(id)) {
                if (i === 0) {
                    newGroupId = groupId;
                }
                i++;
                const layer = this.service.flatLayer.getLayer(id);
                if (!layer) return;
                const { left, top, width, height, rotate } = layer;
                const position = getRotatePosition({ left, top, width, height, rotate });
                positions.push(...position);
                layer.parentId = newGroupId;
                children.push(layer);
            }
        });
        if (!positions.length || !newGroupId) {
            return null;
        }
        const minX = minBy(positions, (pos) => pos.x) as IPoint;
        const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
        const minY = minBy(positions, (pos) => pos.y) as IPoint;
        const maxY = maxBy(positions, (pos) => pos.y) as IPoint;
        const baseInfo = {
            left: minX.x,
            top: minY.y,
            width: maxX.x - minX.x,
            height: maxY.y - minY.y,
            rotate: 0,
            children,
        };
        return getGroupLayerProps({
            id: newGroupId,
            ...baseInfo,
            data: {
                isExtraGroup: true,
            },
        });
    }

    /**
     * 获取图层所属舞台ID
     * @param layerId - 图层ID
     * @returns 所属舞台ID
     */
    getLayerFrameId(layerId: string) {
        return this.service.flatLayer.get(layerId)?.__originIndex;
    }

    /**
     * 获取舞台ID和图层ID列表
     * @param frameId - 舞台ID
     * @param forceAdd - 是否强制添加
     * @returns 舞台ID和图层ID列表
     */
    getFrameIdAndLayerIds(frameId: number, forceAdd = false) {
        let layerIds: string[] = this.__frameLayerIdsList[frameId];
        if (!layerIds) {
            layerIds = [];
            if (forceAdd) {
                this.__frameLayerIdsList.push(layerIds);
                frameId = this.__frameLayerIdsList.length - 1;
            }
        }
        return {
            layerIds,
            frameId,
        };
    }

    /** 获取舞台的基础数据 */
    getFrameTransform(frameIdOrLayerId: number | string, scale = 1) {
        let frameId = 0;
        if (typeof frameIdOrLayerId === 'string') {
            const res = this.getLayerFrameId(frameIdOrLayerId);
            if (isNumber(res)) {
                frameId = res;
            }
        } else {
            frameId = frameIdOrLayerId;
        }
        const { width, height } = this.getSize(frameId);
        let top = 0;
        if (this.service.edgelessCanvas.isLongImageMode) {
            const index = this.__renderFrameIdsList.indexOf(frameId);
            if (index !== -1) {
                const sizeList = this.getSizeList();
                for (let i = 0; i < sizeList.length; i++) {
                    if (i < index) {
                        top += sizeList[i].height;
                    }
                    if (i === index) {
                        break;
                    }
                }
            }
        }
        return {
            width: width * scale,
            height: height * scale,
            /** 长图模式下，舞台的偏移量 */
            offsetTop: top * scale,
            frameId,
        };
    }

    /** 获取舞台尺寸列表 */
    getSizeList(scale = 1) {
        return this.__renderFrameIdsList.map((item) => {
            const { width, height } = this.getBackgroundLayer(item) || { width: 0, height: 0 };
            return { width: width * scale, height: height * scale };
        });
    }

    /** 获取指定渲染的舞台尺寸 */
    getSizeByIndex(index: number) {
        return this.getSize(this.indexToFrameId(index));
    }

    /**
     * 获取舞台尺寸
     * @param frameId - 可选，舞台ID
     * 无 frameId 时：
     *  - 单画布：当前激活的尺寸
     *  - 多画布：总长度
     * 有 frameId 时：
     *  - 获取舞台尺寸
     */
    getSize(frameId?: number): ISize {
        if (isUndefined(frameId)) {
            if (this.service.edgelessCanvas.isLongImageMode) {
                return this.getLongSize();
            }
            return this.getActiveFrameSize();
        }
        const { width, height } = this.getBackgroundLayer(frameId) || {
            width: 0,
            height: 0,
        };

        return { width: ~~width, height: ~~height };
    }

    /** 获取长图尺寸 */
    getLongSize() {
        const sizeList = this.getSizeList();
        return {
            width: sizeList[0]?.width ?? 0,
            height: sizeList.reduce((sum, size) => sum + size.height, 0),
        };
    }

    /** 获取主图层 */
    getMainLayer(frameId: number) {
        const layerIds = this.getLayerIds(frameId);
        for (let i = 1; i < Math.min(layerIds.length, 2); i++) {
            const layer = this.service.flatLayer.get(layerIds[i]);
            if (layer) {
                if (this.service.behavior.get(layer)?.isMainLayer?.()) {
                    return layer as ILayerProps;
                }
            }
        }
        return null;
    }

    /** 获取背景图层 */
    getBackgroundLayer(frameId: number) {
        const layerIds = this.getLayerIds(frameId);
        for (let i = 0; i < Math.min(layerIds.length, 1); i++) {
            const layer = this.service.flatLayer.get(layerIds[i]);
            if (layer) {
                if (this.service.behavior.get(layer)?.isBackgroundLayer?.()) {
                    return layer as ILayerProps;
                }
            }
        }
        return null;
    }

    /** 获取画布距离舞台顶部的偏移量 */
    getStageTop(stageIndex: number) {
        let top = 0;
        for (let i = 0; i < stageIndex; i++) {
            const originIndex = this.indexToFrameId(i);
            top += this.getBackgroundLayer(originIndex)?.height || 0;
        }

        return top;
    }

    /** 获取所有舞台 */
    getFrames() {
        return this.__renderFrameIdsList.map((frameId) => this.getLayers(frameId));
    }

    /** 根据舞台ID, 获取舞台图层 */
    getLayers(frameId: number, clone?: boolean, ignoreExtraGroup?: boolean): ILayerProps[];
    getLayers(layerIds: string[], clone?: boolean, ignoreExtraGroup?: boolean): ILayerProps[];
    getLayers(frameIdOrLayerIds: number | string[], clone = true, ignoreExtraGroup: boolean = false) {
        const { flatLayer } = this.service;
        let layerIds: string[] = [];
        if (isNumber(frameIdOrLayerIds)) {
            layerIds = this.getFrameIdAndLayerIds(frameIdOrLayerIds).layerIds;
        } else {
            layerIds = frameIdOrLayerIds;
        }
        if (clone) {
            return this.__resetExtraGroupInfo(layerIds, (id) => cloneDeep(flatLayer.get(id))).layers;
        }
        const layers: ILayerProps[] = [];
        for (let i = 0; i < layerIds.length; i++) {
            const layer = flatLayer.get(layerIds[i]);
            if (layer && (!ignoreExtraGroup || !flatLayer.checkIsExtraGroup(layer))) {
                layers.push(layer);
            }
        }
        return layers;
    }

    /** 获取平铺图层 */
    getFlatLayers(frameId: number, ignoreLayerIds: string[] = []) {
        const { flatLayer } = this.service;
        const layerIds = this.getLayerIds(frameId);
        const layers: ILayerProps[] = [];
        const traverse = (layer: ILayerProps) => {
            if (!layer) return;
            if (!flatLayer.checkIsGroup(layer) && indexOf(ignoreLayerIds, layer.id) === -1) {
                layers.push(layer);
            }
            if (layer.children) {
                layer.children.forEach(traverse);
            }
        };
        for (let i = 0; i < layerIds.length; i++) {
            traverse(flatLayer.get(layerIds[i]));
        }
        return layers;
    }

    /** 根据渲染索引, 获取舞台图层 */
    getLayersByIndex(index: number) {
        const frameId = this.indexToFrameId(index);
        return this.getLayers(frameId);
    }

    /** 获取舞台图层ID列表 */
    getLayerIds(frameId: number) {
        return this.getFrameIdAndLayerIds(frameId).layerIds;
    }

    /** 获取舞台渲染id列表，不包含临时组 */
    getRenderIdsWithoutExtraGroup(frameId: number) {
        const { flatLayer } = this.service;
        return this.getLayerIds(frameId).filter((id) => !flatLayer.checkIsExtraGroup(flatLayer.getLayer(id)));
    }

    /** 获取舞台渲染id列表，只包含临时组 */
    getRenderIdsOnlyExtraGroup(frameId: number) {
        const { flatLayer } = this.service;
        return this.getLayerIds(frameId).filter((id) => flatLayer.checkIsExtraGroup(flatLayer.getLayer(id)));
    }

    /** 获取所有渲染舞台ID列表 */
    getRenderFrameIds() {
        return [...this.__renderFrameIdsList];
    }

    /** 获取舞台渲染索引长度 */
    getFrameIdsLength() {
        return this.__renderFrameIdsList.length;
    }

    /** 设置舞台渲染ID列表 */
    setFrameIds(frameIds: number[]) {
        const oldFrameIds = [...this.__renderFrameIdsList];
        this.__renderFrameIdsList.length = 0;
        this.__renderFrameIdsList.push(...frameIds);
        const diff = difference(this.__renderFrameIdsList, oldFrameIds);
        this.__emitRerenderAll(diff);
    }

    /** 添加舞台渲染ID列表 */
    addFrameIds(...frameIds: number[]) {
        this.__renderFrameIdsList.push(...frameIds);
        this.__emitRerenderAll(frameIds);
    }

    /** 删除舞台渲染ID列表 */
    removeFrameIds(...frameIds: number[]) {
        pull(this.__renderFrameIdsList, ...frameIds);
        this.__emitRerenderAll([]);
    }

    /** 获取舞台原始id列表 */
    getLayerIdsList() {
        return this.__frameLayerIdsList;
    }

    /**
     * 获取碰撞图层
     *
     * @param frameId - 舞台ID
     * @param ignoreLock - 可选；是否忽略锁定图层；默认值为 `true`
     * @param useTryPick - 可选；是否使用 tryPick 检测；默认值为 `false`
     */
    getCollisionLayers(frameId: number, ignoreLock: boolean = true, useTryPick: boolean = false) {
        const { flatLayer, behavior } = this.service;
        const layers = this.getLayers(frameId, false);
        const res: ILayerProps[] = [];
        for (let i = 0; i < layers.length; i++) {
            const layer = layers[i];
            const skip = (ignoreLock && layer.lock) || flatLayer.checkIsNeedHidden(layer);
            if (!skip) {
                if (!flatLayer.checkIsGroupExtension(layer) && flatLayer.checkIsGroup(layer)) {
                    if (flatLayer.checkIsExtraGroup(layer)) {
                        layer.children?.forEach((item) => {
                            res.push(item);
                        });
                    } else {
                        res.push(layer);
                    }
                } else {
                    if (useTryPick && behavior.get(layer)?.checkTryPick) {
                        if (behavior.get(layer)?.checkTryPick?.(layer)) {
                            res.push(layer);
                        }
                    } else {
                        const disableInGroup = behavior.get(layer)?.checkDisableInGroup?.(layer);
                        if (!disableInGroup) {
                            res.push(layer);
                        }
                    }
                }
            }
        }
        return res;
    }

    /** 获取图层重叠列表 */
    getLayerOverLapList(layerId: string, ignoreTypes: LayerType[] = []) {
        const { flatLayer } = this.service;
        const layer = flatLayer.getLayer(layerId, true);
        const frameId = this.getLayerFrameId(layerId) || 0;
        const layerPolygon = convertToPolygon(layer);
        const layerIds = this.getLayerIds(frameId);
        const overLapList: ILayerProps[] = [];

        // 只插入一级组，其子组元素全部插入一级组
        const setChildren = (layer: ILayerProps, targetArr?: ILayerProps[]) => {
            const children: ILayerProps[] = targetArr ?? [];
            (layer.children || []).forEach((item) => {
                const res = flatLayer.getLayer(item.id, true);
                if (canInsert(res)) {
                    if (res.layerType === LayerType.GROUP_LAYER) {
                        setChildren(res, children);
                    } else {
                        children.unshift(res);
                    }
                }
            });
            if (!targetArr) layer.children = children;
        };

        // 判断是否与图层相交可以插入
        const canInsert = (curLayer: ILayerProps) => {
            if (!curLayer) return;
            const curLayerPolygon = convertToPolygon(curLayer);
            if (!flatLayer.checkIsNeedHidden(curLayer) && layerPolygon.polygon.collidesWith(curLayerPolygon.polygon)) {
                return true;
            }
            return false;
        };

        for (let i = 0; i < layerIds.length; i++) {
            const id = layerIds[i];
            const item = flatLayer.getLayer(id, true);
            if (!item || item.parentId || indexOf(ignoreTypes, item.layerType) !== -1) continue;
            if (flatLayer.checkIsNeedHidden(item)) continue;
            // 全屏水印没有重叠选择的必要, 改到图层behavior中处理
            // if (item.layerType === LayerType.WATERMARK && (item.data as IWatermarkLayerProps).isFullScreen) continue;
            const disableCollision = this.service.behavior.get(item)?.checkDisableCollision?.(item);
            if (disableCollision) continue;
            const itemPolygon = convertToPolygon(item);
            if (layerPolygon.polygon.collidesWith(itemPolygon.polygon)) {
                setChildren(item);
                overLapList.push(item);
            }
        }
        return overLapList;
    }

    /**
     * 获取图层排序列表
     * @param layerId - 图层ID
     * @param ignoreTypes - 忽略的图层类型
     * @returns 排序列表
     */
    getLayerSortData(layerId: string, ignoreTypes: LayerType[] = []) {
        const { flatLayer } = this.service;
        // 获取跟图层交叉的图层
        const layer = flatLayer.getLayer(layerId);
        const childIds = flatLayer.checkIsExtraGroup(layer) ? layer.children?.map((item) => item.id) : [layer.id];
        const layerPolygon = convertToPolygon(layer);
        const frameId = this.getLayerFrameId(layerId) || 0;
        const layerIds = this.getLayerIds(frameId);
        const sortList: { id: string; index: number }[] = [];
        const childList: { id: string; index: number }[] = [];
        for (let i = 0; i < layerIds.length; i++) {
            const id = layerIds[i];
            const item = flatLayer.getLayer(id);
            let parentIsFormalGroup = false;
            if (item?.parentId) {
                const parent = flatLayer.getLayer(item.parentId);
                parentIsFormalGroup = flatLayer.checkIsFormalGroup(parent);
            }
            if (!item || (item.parentId && parentIsFormalGroup) || indexOf(ignoreTypes, item.layerType) !== -1)
                continue;
            if (flatLayer.checkIsExtraGroup(item) || flatLayer.checkIsNeedHidden(item)) continue;
            if (this.service.behavior.get(item)?.checkDisableCollision?.(item)) continue;
            const itemPolygon = convertToPolygon(item);
            if (layerPolygon.polygon.collidesWith(itemPolygon.polygon)) {
                const index = indexOf(layerIds, item.id);
                const data = {
                    id: item.id,
                    index,
                };
                if (childIds.indexOf(item.id) !== -1) {
                    childList.push(data);
                } else {
                    sortList.push(data);
                }
            }
        }
        const topItem = childList[childList.length - 1];
        const bottomItem = childList[0];
        // 可下移几个
        let downCount = 0;
        let upCount = 0;
        for (let i = 0; i < sortList.length; i++) {
            const item = sortList[i];
            if (item.index < topItem.index) {
                downCount++;
            }
            if (item.index > bottomItem.index) {
                upCount++;
            }
        }
        const disableDown = downCount === 0;
        const disableUp = upCount === 0;
        const disableSort = disableDown && disableUp;
        return {
            data: {
                /** 原始索引 */
                frameId,
                /** 排序列表 */
                sortList,
                /** 子列表 */
                childList,
            },
            cursor: {
                /** 当前索引 */
                current: 0,
                /** 可下移 */
                downCount: -downCount,
                /** 可上移 */
                upCount,
            },
            /** 操作 */
            operate: {
                /** 下移 */
                disableDown,
                /** 上移 */
                disableUp,
                /** 禁用排序 */
                disableSort,
            },
        };
    }

    /**
     * 添加舞台额外信息
     *
     * @param extraInfo - 额外信息
     */
    addExtraInfos(extraInfos: IExtraInfo[], frameIds?: number[]) {
        if (frameIds && frameIds.length) {
            for (let i = 0; i < frameIds.length; i++) {
                const frameId = frameIds[i];
                this.__extraInfos[frameId] = extraInfos[i];
            }
        } else {
            this.__extraInfos.push(...extraInfos);
        }
        this.__emitAddExtraInfos(extraInfos, frameIds);
        // console.log('addExtraInfos===>2', this.__extraInfos);
    }

    /** 监听舞台额外信息添加 */
    onAddExtraInfos(fn: (extraInfos: IExtraInfo[], frameIds?: number[]) => void, weight?: number) {
        return this.service.events.on(addExtraInfosEventName, fn, weight);
    }

    /** 派发舞台额外信息添加 */
    private __emitAddExtraInfos(extraInfos: IExtraInfo[], frameIds?: number[]) {
        this.service.events.emit(addExtraInfosEventName, extraInfos, frameIds);
    }

    /**
     * 设置舞台额外信息
     *
     * @param index - 舞台索引
     * @param extraInfo - 额外信息
     */
    setExtraInfo(index: number, extraInfo: IExtraInfo) {
        const frameId = this.indexToFrameId(index);
        this.__extraInfos[frameId] = extraInfo;
    }

    /**
     * 设置图层额外信息
     *
     * @param layerId - 图层id
     * @param extraInfo - 额外信息
     */
    setExtraInfoByLayerId(layerId: string, extraInfo: IExtraInfo) {
        const frameId = this.getLayerFrameId(layerId);
        if (isNumber(frameId)) {
            this.__extraInfos[frameId] = extraInfo;
        }
    }

    /**
     * 获取舞台额外信息
     *
     * @param index - 舞台索引
     */
    getExtraInfo(index: number) {
        const frameId = this.indexToFrameId(index);
        return this.getExtraInfoByFrameId(frameId);
    }

    /** 获取舞台额外信息列表 */
    getExtraInfoList() {
        // console.log('getExtraInfoList===>', this.__renderFrameIdsList);
        return this.__renderFrameIdsList.map((frameId) => {
            return this.getExtraInfoByFrameId(frameId);
        });
    }

    /** 获取舞台额外信息 */
    getExtraInfoByFrameId(frameId: number) {
        return cloneDeep(this.__extraInfos[frameId]) || {};
    }

    /** 获取快照 */
    getSnapshot() {
        const snapshot = {
            data: cloneDeep(this.service.flatLayer.data),
            extraInfos: cloneDeep(this.__extraInfos),
            originIdsList: cloneDeep(this.__frameLayerIdsList),
            renderStageList: cloneDeep(this.__renderFrameIdsList),
        };
        // 剔除临时组信息
        snapshot.originIdsList.forEach((originIds) => {
            const { extraGroupIds } = this.__resetExtraGroupInfo(originIds, (id) => snapshot.data[id]);
            const extraGroupIdsList = Array.from(extraGroupIds);
            pullAll(originIds, extraGroupIdsList);
            extraGroupIdsList.forEach((id) => {
                delete snapshot.data[id];
            });
        });
        // console.log('ot getScreenshot 3', screenshot);

        return snapshot;
    }

    /** 恢复快照 */
    restoreSnapshot(snapshot: ReturnType<typeof this.getSnapshot>) {
        this.service.flatLayer.data = snapshot.data;
        this.__extraInfos.length = 0;
        this.__extraInfos.push(...snapshot.extraInfos);
        this.__frameLayerIdsList.length = 0;
        this.__frameLayerIdsList.push(...snapshot.originIdsList);
        this.setFrameIds(snapshot.renderStageList);
        this.service.edgelessCanvas.rebuildObjectsFromFlatLayer();
    }

    /** 重置临时组信息 */
    private __resetExtraGroupInfo(ids: string[], getLayer: (id: string) => ILayerProps) {
        const { flatLayer } = this.service;
        const layers: ILayerProps[] = [];
        const extraGroupIds: Set<string> = new Set();
        const groupIds: Set<string> = new Set();
        ids.forEach((id: string) => {
            const layer = flatLayer.getLayerWithClone(getLayer(id));
            if (!layer) return;
            if (layer.parentId) {
                if (!extraGroupIds.has(layer.parentId) && !groupIds.has(layer.parentId)) {
                    const parent = flatLayer.get(layer.parentId);
                    if (parent) {
                        if (flatLayer.checkIsExtraGroup(parent)) {
                            extraGroupIds.add(parent.id);
                        } else {
                            groupIds.add(parent.id);
                        }
                    }
                }
                if (extraGroupIds.has(layer.parentId)) {
                    layer.parentId = '';
                }
            }
            if (!flatLayer.checkIsExtraGroup(layer)) {
                layers.push(layer);
            }
        });
        return {
            layers,
            extraGroupIds,
            groupIds,
        };
    }
}
